if (!customElements.get('custom-variant-picker')) { 
  class CustomVariantPicker extends HTMLElement {
    constructor() {
      super();

      this.preSelectedOptions = {};
      this.isApplyingPreselectedOptions = false;
      this.selectionUrlTimer = null;
      this.hasError = false;
      this.errorMessageType = 'none';
      this.errorMessage = "";
      this.errorContactMessage = "";
      this.errorMessageList = [];
      this.products = [];
      this.optionContainers = [];
      this.boundOptionInputs = new WeakSet();
      this.boundOptionTitles = new WeakSet();
      this.preselectionSwatchLoads = [];
      this.checkboxOptions = [];
      this.dropdownOptions = [];
      this.colorOptions = [];
      this.galleryImages = [];
      this.cdnImages = [];
      this.lastCandidateMatches = [];
      this.pillOptions = [];
      this.basicOptions = [];
    }

    static get IMAGE_ANGLE_SUFFIXES() {
      return ['front', 'back', 'side', 'detail'];
    }

    static get CDN_BASE_URL() {
      return 'https://cdn.admiral-outdoor.com';
    }

    connectedCallback() {
      this.init();
    }

    async init() {
      this.isApplyingPreselectedOptions = true;
      this.buildQueryParamsObject();
      this.buildErrorStates();
      this.loadProducts();
      this.loadOptionContainers();
      this.loadGalleryImages();
      this.loadOptionsUI();
      this.initChangeEvents();
      this.showHiddenSwatches();
      this.updateMetafieldRenderDebug();
      this.syncGalleryImage();
      this.loadCdnGalleryImages();
      this.updateCalculatedSku();
      try {
        await this.applyPreSelectedOptions();
      } finally {
        this.isApplyingPreselectedOptions = false;
      }
      this.updateSelectionUrl();
    }

    // updates the url based on the current UI configuration
    updateSelectionUrl() {
      // make sure we don't run this while another change is happening
      if ( this.isApplyingPreselectedOptions ) {
        return
      }

      // Several controls can change together. Rebuild once after their handlers finish.
      clearTimeout(this.selectionUrlTimer);

      this.selectionUrlTimer = setTimeout(function () {
        if ( this.isApplyingPreselectedOptions ) {
          return
        }

        const baseVariant = this.getBaseSelectedVariant();
        if ( !baseVariant ) {
          return
        }

        const url = new URL(window.location.href);
        const currentKeys = Array.from(url.searchParams.keys());

        // Preserve tracking, preview and other parameters unrelated to selections
        for ( const key of currentKeys ) {
          if ( key == 'variant' || key.startsWith('option[') || key.startsWith('addon[') ) {
            url.searchParams.delete(key);
          }
        }

        url.searchParams.set('variant', String(baseVariant.id));
        const addonOptions = new Map();
        const wrappers = this.querySelectorAll('.option-wrapper[data-option-scope]');

        for ( const wrapper of wrappers ) {
          // A collapsed swatch palette is still active; a hidden dependent group isn't.
          if ( wrapper.closest('.option-wrapper.hidden') ) {
            continue
          }

          const scope = wrapper.dataset.optionScope;
          if ( scope != 'option' && scope != 'addon' ) {
            continue
          }

          let variantId = null;
          if ( scope == 'addon' ) {
            const rawSelect = this.getAdvancedRawSelect(wrapper.dataset.productOptionFor, wrapper);
            
            if ( !rawSelect || !rawSelect.value ) {
              continue
            }

            variantId = rawSelect.value;

            if ( !addonOptions.has(variantId) ) {
              addonOptions.set(variantId, new Map());
            }
          }

          // These handles preserve the specific colors that share a Shopify variant.
          const swatches = wrapper.querySelectorAll('input[data-option-value]:checked');
          for ( const swatch of swatches ) {
            if ( swatch.closest('.option-wrapper') !== wrapper ) {
              continue
            }

            const optionName = swatch.dataset.relatedToOption || swatch.dataset.optionMetafieldName;
            if ( !optionName ) {
              continue
            }

            const key = this.handleize(optionName);
            const value = swatch.dataset.optionValue;
            if ( scope == 'option' ) {
              url.searchParams.set(`option[${key}]`, value);
            } else {
              addonOptions.get(variantId).set(key, value);
            }
          }
        }

        for ( const [variantId, options] of addonOptions ) {
          if ( options.size == 0 ) {
            // Both Yes and No variants use =true: the ID identifies the selection.
            url.searchParams.set(`addon[${variantId}]`, 'true');
          } else {
            for ( const [key, value] of options ) {
              url.searchParams.set(`addon[${variantId}][${key}]`, value);
            }
          }
        }

        // Keep selection-key brackets readable while leaving values safely encoded.
        const queryParts = [];
        for ( const [key, value] of url.searchParams ) {
          const encodedParam = new URLSearchParams();
          encodedParam.append(key, value);
          let queryPart = encodedParam.toString();

          if ( key.startsWith('option[') || key.startsWith('addon[') ) {
            const equalsIndex = queryPart.indexOf('=');
            const readableKey = queryPart.slice(0, equalsIndex).replace(/%5B/g, '[').replace(/%5D/g, ']');
            queryPart = readableKey + queryPart.slice(equalsIndex);
          }

          queryParts.push(queryPart);
        }

        const newUrl = url.origin + url.pathname + '?' + queryParts.join('&') + url.hash;
        if ( newUrl != window.location.href ) {
          window.history.replaceState(window.history.state, '', newUrl);
        }
      }.bind(this), 0);
    }

    loadOptionContainers() {
      this.optionContainers = [];
      const wrappers = this.querySelectorAll('.option-wrapper[data-option-scope]');

      for (let i = 0; i < wrappers.length; i++) {
        const wrapper = wrappers[i];
        const variantIds = [];

        try {
          const parsedIds = JSON.parse(wrapper.dataset.optionVariantIds || '[]');
          if (Array.isArray(parsedIds)) {
            for (let j = 0; j < parsedIds.length; j++) {
              variantIds.push(String(parsedIds[j]));
            }
          }
        } catch (error) {
          console.warn('[Custom Variant Picker] Invalid option variant IDs:', wrapper, error);
        }

        this.optionContainers.push({
          element: wrapper,
          scope: wrapper.dataset.optionScope,
          productId: wrapper.dataset.productOptionFor,
          variantIds: variantIds
        });
      }
    }

    buildQueryParamsObject() {
      const params = new URLSearchParams(window.location.search);
      if ( params.size === 0 ) return;

      const tempObject = {
        option: {},
        addon: {}
      }

      for ( const [key, value] of params ) {

        let realKey = ""
        if ( key.indexOf('option[') > -1 ) {
          realKey = key.replace('option[', "").replace(']', "");
          tempObject.option[realKey] = this.handleize(value);
        } else if ( key.indexOf('addon[') > -1 ) {
          const count = key.split('[').length - 1;
          if ( count > 1 ) {
            const keyParts = key.split('[');
            const keyPart1 = keyParts[1].replace(']', "");
            const keyPart2 = keyParts[2].replace(']', "");
            if ( !tempObject.addon[keyPart1] || typeof tempObject.addon[keyPart1] !== 'object' ) {
              tempObject.addon[keyPart1] = {};
            }
            tempObject.addon[keyPart1][keyPart2] = this.handleize(value);
          } else {
            realKey = key.replace('addon[', "").replace(']', "");
            tempObject.addon[realKey] = this.handleize(value);
          }
          
        }
      }

      this.preSelectedOptions = tempObject;
    }

    buildErrorStates() {
      this.errorMessageType = this.dataset.preselectOptionsErrorMessageType || 'none';
      this.errorMessage = this.dataset.preselectOptionsErrorMessage || '';
      this.errorContactMessage = this.dataset.preselectOptionsErrorContactMessage || '';
    }

    createMessageDiv() {
      const message = document.createElement('div');
      message.className = 'preselected-options-error';
      message.dataset.preselectionError = '';
      message.setAttribute('role', 'status');

      return message;
    }

    displayPreselectionErrors() {
      if ( this.errorMessageType === 'none' || !this.hasError ) return;

      let primaryMessage = null;

      if ( this.errorMessageType === 'list' ) {
        const message = this.createMessageDiv();
        message.classList.add('preselected-options-error--list');
        
        const list = document.createElement('ul');
        const displayedMessages = [];
        for ( let i = 0; i < this.errorMessageList.length; i++ ) {
          const text = this.errorMessageList[i].message;

          // skip duplicate messages
          if ( displayedMessages.includes(text) ) continue;

          displayedMessages.push(text);

          const item = document.createElement('li');
          item.textContent = text;

          list.appendChild(item);
        }

        message.appendChild(list);

        this.prepend(message);
        primaryMessage = message;
      } else if ( this.errorMessageType === 'general' && this.errorMessage ) {
        const message = this.createMessageDiv();
        message.textContent = this.errorMessage;

        this.prepend(message);
        primaryMessage = message;
      } else if ( this.errorMessageType === 'per_option' && this.errorMessage ) {
        const containers = [];
        for ( let i = 0; i < this.errorMessageList.length; i++ ) {
          const container = this.errorMessageList[i].container || this;
          let titleWrapper = null;
          if (container !== this) {
            titleWrapper = container.querySelector('.option-title-wrapper');
          }

           // skip duplicate messages
          if ( containers.includes(container) ) continue;

          containers.push(container);

          const message = this.createMessageDiv();
          message.textContent = this.errorMessage;

          if (titleWrapper) {
            titleWrapper.appendChild(message);
          } else {
            container.prepend(message);
          }
        }
      }

      if (this.errorContactMessage) {
        const contactMessage = this.createMessageDiv();
        contactMessage.textContent = this.errorContactMessage;

        if (primaryMessage) {
          primaryMessage.insertAdjacentElement('afterend', contactMessage);
        } else {
          this.prepend(contactMessage);
        }
      }
    }

    formatOptionLabel( value ) {
      const words = String(value).trim().toLowerCase().split("-");

      for ( let i = 0; i < words.length; i++ ) {
        words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1);
      }

      return words.join(' ');
    }

    handleize( value ) {
      const lowerTrimmedValue = String(value).trim().toLowerCase();
      const handleizedValue = lowerTrimmedValue.replace(/[\s_]+/g, '-');

      return handleizedValue;
    }

    recordPreselectionError(message, container = null) {
      this.hasError = true;
      this.errorMessageList.push({ message: message, container: container });
    }

    reportOptionSelectionError(scope, key, value, addonId = null) {
      const inputs = scope.querySelectorAll('input[data-related-to-option], input[data-option-metafield-name], select.option-selector');
      
      let matchingInput = null;
      
      for ( let i = 0; i < inputs.length; i++ ) {
        const input = inputs[i];
        const wrapper = input.closest('.option-wrapper');
        
        if ( !addonId && wrapper.dataset.optionScope !== 'option' ) {
          continue
        }

        const optionName = this.handleize(input.dataset.relatedToOption || '');
        const metafieldName = this.handleize(input.dataset.optionMetafieldName || '');
        
        if ( optionName === this.handleize(key) || metafieldName === this.handleize(key) ) {
          matchingInput = input;
          break
        }
      }

      let message = `${this.formatOptionLabel(key)} not found`;
      let container = addonId ? scope : null;
      
      if ( matchingInput ) {
        message = `${this.formatOptionLabel(value)} is not an option for ${this.formatOptionLabel(key)}`;
        container = matchingInput.closest('.option-container') || matchingInput.closest('.option-wrapper');
      }
      
      if ( addonId ) {
        message = `Add-on ${addonId}: ${message}`;
      }
      
      this.recordPreselectionError(message, container);
    }

    async findPreselectedSwatch(scope, key, value, mainOption = false) {
      let selector = `input[data-related-to-option="${CSS.escape(key)}"][data-option-value="${CSS.escape(value)}"]`;
      
      if ( mainOption ) {
        selector = '.option-wrapper[data-option-scope="option"] ' + selector;
      }

      let input = scope.querySelector(selector);
      if ( input ) {
        return input;
      }

      const buttons = scope.querySelectorAll('.option-wrapper__view-more-wrapper');
      for ( let i = 0; i < buttons.length; i++ ) {
        const button = buttons[i];
        const wrapper = button.closest('.option-wrapper');

        if ( mainOption && wrapper.dataset.optionScope !== 'option' ) {
          continue
        }

        if ( this.handleize(button.dataset.metafieldKey || '') !== this.handleize(key) ) {
          continue
        }
        if ( parseInt(button.dataset.totalMetaobjects) <= 50 || button.dataset.hasLoadedAll === 'true' ) {
          continue
        }

        // Use the same loader as View All, and wait before checking the HTML again.
        const loadInfo = {
          scope: scope,
          key: key,
          value: value,
          mainOption: mainOption,
          status: 'Additional colors needed (View All).'
        };
        if (this.dataset.preselectShowDebug === 'true') this.preselectionSwatchLoads.push(loadInfo);
        try {
          await loadMoreSwatchesFromViewAll(button);
        } catch (error) {
          loadInfo.status = 'Additional colors needed; loading failed.';
          throw error;
        }

        input = scope.querySelector(selector);
        
        if ( input ) {
          loadInfo.status = 'Loaded additional colors via View All; requested color found.';
          return input;
        }

        if ( button.dataset.hasLoadedAll !== 'true' ) {
          loadInfo.status = 'Additional colors needed; loading failed.';
          throw new Error('The swatches could not be loaded');
        }
        loadInfo.status = 'Loaded additional colors via View All; requested color not found.';
      }
      return null;
    }

    getPreselectionDebugRow(key, value) {
      const row = { parameter: `${key}=${value}`, item: '', expected: '', actual: '' };
      const optionMatch = key.match(/^option\[([^\]]+)\]$/);
      const addonMatch = key.match(/^addon\[(\d+)\](?:\[([^\]]+)\])?$/);
      let scope = this;
      let variant = null;

      if (addonMatch || key === 'variant') {
        const variantId = addonMatch ? addonMatch[1] : value;
        for (let i = 0; i < this.products.length; i++) {
          const product = this.products[i];
          for (let j = 0; j < product.variants.length; j++) {
            if (String(product.variants[j].id) === variantId) {
              variant = product.variants[j];
              row.item = variant.product_title || `Product ${product.id}`;
              break;
            }
          }
          if (variant) break;
        }
        if (addonMatch) {
          scope = null;
          for (let i = 0; i < this.optionContainers.length; i++) {
            const container = this.optionContainers[i];
            if (container.scope === 'addon' && container.variantIds.includes(variantId)) {
              scope = container.element;
              break;
            }
          }
          if (!scope) {
            row.item = row.item || `Add-on ${variantId}`;
            row.expected = 'Select this add-on';
            row.actual = 'Container not found';
            return row;
          }
        } else {
          row.expected = variant ? variant.title : 'Variant not found';
          return row;
        }
      }

      const optionKey = optionMatch ? optionMatch[1] : addonMatch ? addonMatch[2] : null;
      if (optionKey) {
        const inputs = scope.querySelectorAll('input[data-related-to-option], input[data-option-metafield-name], select.option-selector');
        let control = null;
        let selected = null;
        for (let i = 0; i < inputs.length; i++) {
          const input = inputs[i];
          if (optionMatch && input.closest('.option-wrapper').dataset.optionScope !== 'option') continue;
          if (this.handleize(input.dataset.relatedToOption || '') !== this.handleize(optionKey) &&
              this.handleize(input.dataset.optionMetafieldName || '') !== this.handleize(optionKey)) continue;
          control = control || input;
          if (input.checked || input.tagName === 'SELECT') selected = input;
        }
        row.expected = this.formatOptionLabel(this.handleize(value));
        if (!control) {
          row.item = `${row.item ? row.item + ' — ' : ''}${this.formatOptionLabel(optionKey)}`;
          row.actual = 'Option not found';
          return row;
        }
        const container = control.closest('.option-container') || control.closest('.option-wrapper');
        const label = container.querySelector('.option-title');
        row.item = `${addonMatch && row.item ? row.item + ' — ' : ''}${label ? label.textContent.trim() : this.formatOptionLabel(optionKey)}`;
        if (selected) {
          const swatch = selected.closest('[data-swatch-name]');
          row.actual = selected.tagName === 'SELECT' ? selected.options[selected.selectedIndex]?.textContent.trim() :
            swatch ? swatch.dataset.swatchName : this.formatOptionLabel(selected.dataset.optionValue || selected.value);
        } else {
          row.actual = 'Nothing selected';
        }
      } else if (addonMatch) {
        const checkbox = scope.querySelector('input.checkbox-input');
        if (checkbox) {
          const label = scope.querySelector('label');
          row.item = label ? label.textContent.trim() : row.item;
          row.expected = value === 'false' || variant?.title.toLowerCase() === 'no' ? 'Not checked' : 'Checked';
          row.actual = checkbox.checked ? 'Checked' : 'Not checked';
        } else {
          const label = scope.querySelector('.option-title');
          row.item = label ? label.textContent.trim() : row.item;
          row.expected = variant ? variant.title : 'Variant not found';
          const rawSelect = scope.querySelector('select.raw-data');
          row.actual = rawSelect?.selectedOptions[0]?.textContent.trim() || 'Selection unavailable';
        }
      } else {
        row.item = 'Not a preselection parameter';
        row.expected = 'Ignored';
      }
      return row;
    }

    updatePreselectionDebugVariants() {
      if (this.dataset.preselectShowDebug !== 'true' || !this.preselectionDebugVariants?.isConnected) return;

      const container = this.preselectionDebugVariants;
      container.replaceChildren();
      const heading = document.createElement('h3');
      heading.textContent = 'Variants — Add to Cart selection';
      container.appendChild(heading);
      const selectedVariants = this.getSelectedVariants();

      for (let i = 0; i < this.products.length; i++) {
        const product = this.products[i];
        const title = document.createElement('h4');
        title.textContent = product.variants[0]?.product_title || `Product ${product.id}`;
        container.appendChild(title);
        const list = document.createElement('ul');

        for (let j = 0; j < product.variants.length; j++) {
          const variant = product.variants[j];
          let selected = false;
          for (let k = 0; k < selectedVariants.length; k++) {
            if (String(selectedVariants[k].id) === String(variant.id)) {
              selected = true;
              break;
            }
          }
          const item = document.createElement('li');
          item.textContent = `${variant.title} (${variant.id})${selected ? ' SELECTED' : ''}`;
          if (selected) item.style.fontWeight = '700';
          list.appendChild(item);
        }
        container.appendChild(list);
      }
    }

    showPreselectionDebug() {
      if (this.dataset.preselectShowDebug !== 'true') return;
      if (this.preselectionDebugPanel) this.preselectionDebugPanel.remove();
      const panel = document.createElement('section');
      panel.className = 'preselection-debug-panel';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', 'Preselected options debug');
      const heading = document.createElement('h2');
      heading.textContent = 'Preselected options debug';
      panel.appendChild(heading);
      const close = document.createElement('button');
      close.type = 'button';
      close.textContent = 'Close';
      close.addEventListener('click', function () { panel.remove(); });
      panel.appendChild(close);
      panel.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') panel.remove();
      });
      const note = document.createElement('p');
      note.textContent = 'Testing only — never enable this in the LIVE theme. Expected and current selections are shown below.';
      panel.appendChild(note);
      const params = new URLSearchParams(window.location.search);
      for (const [key, value] of params) {
        const row = this.getPreselectionDebugRow(key, value);
        const entry = document.createElement('div');
        entry.className = 'preselection-debug-entry';
        const parameter = document.createElement('code');
        parameter.textContent = row.parameter;
        entry.appendChild(parameter);
        const detail = document.createElement('p');
        detail.textContent = `${row.item}\nExpected: ${row.expected}${row.actual ? '\nCurrent: ' + row.actual : ''}`;
        const optionMatch = key.match(/^option\[([^\]]+)\]$/);
        const addonMatch = key.match(/^addon\[(\d+)\]\[([^\]]+)\]$/);
        for (let i = 0; i < this.preselectionSwatchLoads.length; i++) {
          const loadInfo = this.preselectionSwatchLoads[i];
          let matchesScope = Boolean(optionMatch && loadInfo.mainOption);
          if (addonMatch && !loadInfo.mainOption) {
            for (let j = 0; j < this.optionContainers.length; j++) {
              const container = this.optionContainers[j];
              if (container.element === loadInfo.scope && container.variantIds.includes(addonMatch[1])) {
                matchesScope = true;
                break;
              }
            }
          }
          const optionKey = optionMatch ? optionMatch[1] : addonMatch ? addonMatch[2] : null;
          if (matchesScope && this.handleize(optionKey) === this.handleize(loadInfo.key) && this.handleize(value) === this.handleize(loadInfo.value)) {
            detail.textContent += '\n' + loadInfo.status;
          }
        }
        entry.appendChild(detail);
        panel.appendChild(entry);
      }
      if (params.size === 0) {
        const empty = document.createElement('p');
        empty.textContent = 'No query parameters found.';
        panel.appendChild(empty);
      }
      for (let i = 0; i < this.errorMessageList.length; i++) {
        const error = document.createElement('p');
        error.textContent = `Error: ${this.errorMessageList[i].message}`;
        panel.appendChild(error);
      }
      this.preselectionDebugPanel = panel;
      document.body.appendChild(panel);
      this.preselectionDebugVariants = document.createElement('div');
      this.preselectionDebugVariants.className = 'preselection-debug-entry';
      panel.appendChild(this.preselectionDebugVariants);
      this.updatePreselectionDebugVariants();

      if (!this.preselectionDebugChangeBound) {
        this.addEventListener('change', function () {
          // Let all variant handlers finish synchronizing the hidden selectors.
          clearTimeout(this.preselectionDebugChangeTimer);
          this.preselectionDebugChangeTimer = setTimeout(this.updatePreselectionDebugVariants.bind(this), 0);
        }.bind(this));
        this.preselectionDebugChangeBound = true;
      }
    }

    async applyPreSelectedOptions() {
      if ( Object.keys(this.preSelectedOptions).length === 0 ) {
        this.showPreselectionDebug();
        return;
      }

      // Params follow this format
      // option[frankford-pole-color]=heather-willow <-- This is a product variant
      // &addon[52353193279778]=true  <-- this can be either a checkbox (product) or a selected radio / dropdown value
      // &addon[52353340539170][frankford-canopy-color]=captain-navy  <-- this is a color selector for a product

      // HTML
      // Option Wrapper notes if the item is an option or addon and what type of element is being used (dropdown, checkbox, pills)
      // Addon Wrapper has list of variant ids
      // Hidden elements have the variant id send with textContent that tells us if the checkbox should be open or not, or a matching value that should be selected in the visible element
      

      this.hasError = false;
      this.errorMessageList = [];
      const changedInputs = [];

      if ( Object.keys(this.preSelectedOptions.option || {}).length != 0 ) {
        for ( const [key, value] of Object.entries(this.preSelectedOptions.option) ) {
            // look for matching input on page
            let matchingSelector;
            try {
              matchingSelector = await this.findPreselectedSwatch(this, key, value, true);
            } catch (error) {
              this.recordPreselectionError(`${this.formatOptionLabel(key)} options could not be loaded. Please try View All again.`);
              continue;
            }

            if ( matchingSelector ) {
              matchingSelector.checked = true;
              changedInputs.push(matchingSelector);
            } else {
              this.reportOptionSelectionError(this, key, value);
            }
        }
      }

      if ( Object.keys(this.preSelectedOptions.addon || {}).length != 0 ) {
        for ( const [key, value] of Object.entries(this.preSelectedOptions.addon) ) {
            // Find the matching id in the html, read the inner text
            // if it is a checkbox, check if the inner text is set to Yes
            // otherwise, find the matching input and select it
            if ( value == 'true' ) {
              const matchingHiddenOption = this.querySelector(`.option-wrapper[data-option-scope="addon"] option[value="${CSS.escape(key)}"]`);
  
              if ( matchingHiddenOption ) {
                matchingHiddenOption.selected = true;
                const innerText = matchingHiddenOption.textContent.trim();
  
                // get the parent option wrapper
                const optionWrapper = matchingHiddenOption.closest('.option-wrapper');
           
                let applied = false;
                // handle checkboxes
                if ( optionWrapper.classList.contains('checkbox') ) {
                  const checkbox = optionWrapper.querySelector('input[type="checkbox"]');
                  
                  if ( checkbox ) {
                    checkbox.checked = innerText.toLowerCase() === "yes";
                    changedInputs.push(checkbox);
                    applied = true;
                  }
                  
                // handle dropdowns
                } else if ( optionWrapper.classList.contains('dropdown') ) {
                  const select = optionWrapper.querySelector('select.option-selector');

                  if ( select ) {
                    for (let i = 0; i < select.options.length; i++) {
                      const option = select.options[i];

                      if (option.textContent.trim() === innerText) {
                        select.value = option.value;
                        changedInputs.push(select);
                        applied = true;
                        break
                      }
                    }
                  }

                // all others use an input type with matching value attribute  
                } else {
                  const input = optionWrapper.querySelector(`input[value="${CSS.escape(innerText)}"]`)
                  if ( input ) {
                    input.checked = true;
                    changedInputs.push(input);
                    applied = true;
                  }
                }

                if ( !applied ) {
                  this.recordPreselectionError(`Add-on ${key}: ${innerText} could not be selected`, optionWrapper);
                }
              } else {
                this.recordPreselectionError(`Add-on ${key} not found`);
              }

            } else if ( value !== null && typeof value === 'object' && !Array.isArray(value) ) {
              let matchingOption = null;

              for ( let i = 0; i < this.optionContainers.length; i++ ) {
                const container = this.optionContainers[i];
                if ( container.scope === 'addon' && container.variantIds.includes(String(key)) ) {
                  matchingOption = container.element;
                  break
                }
              }

              if ( !matchingOption ) {
                this.recordPreselectionError(`Add-on ${key} not found`);
                continue
              }

              for ( const [key2, value2] of Object.entries(value) ) {
                let matchingSelector;
                try {
                  matchingSelector = await this.findPreselectedSwatch(matchingOption, key2, value2);
                } catch (error) {
                  this.recordPreselectionError(`Add-on ${key}: ${this.formatOptionLabel(key2)} options could not be loaded. Please try View All again.`, matchingOption);
                  continue;
                }

                if ( matchingSelector ) {
                  matchingSelector.checked = true;
                  changedInputs.push(matchingSelector);
                } else {
                  this.reportOptionSelectionError(matchingOption, key2, value2, key);
                }
              }
            } else {
              this.recordPreselectionError(`Add-on ${key}: invalid selection ${String(value)}`);
            }

        }
      }
      

      for (let i = 0; i < changedInputs.length; i++) {
        changedInputs[i].dispatchEvent(new Event('change', { bubbles: true }));
      }

      if ( this.errorMessageType !== 'none' ) {
        this.displayPreselectionErrors();
      }
      this.showPreselectionDebug();

    }

    loadProducts() {
      const scriptTag = this.querySelector('#all-products');
      if (!scriptTag) {
        console.warn('[Custom Variant Picker] No #all-products script tag found.');
        return;
      }

      try {
        const productsData = JSON.parse(scriptTag.textContent);
        this.products = productsData.products;
        console.log(this.products);
      } catch (error) {
        console.error('[Custom Variant Picker] Failed to parse #all-products JSON:', error);
      }
    }

    loadGalleryImages() {
      const { mediaGalleryEl, thumbSlider } = this.getScopedGalleryEls();
      if (mediaGalleryEl) {
        const slides = mediaGalleryEl.querySelectorAll('.media-gallery__slide[data-media-type="image"]:not([data-cdn-slide])');
        this.galleryImages = Array.from(slides).map((slide, index) => {
          const img = slide.querySelector('img');
          const src = img?.currentSrc || img?.src || '';
          const parsed = this.parseGalleryImageFilename(src);
          const fileWithQuery = src.split('/').pop() || '';
          const fileName = fileWithQuery.split('?')[0];

          return {
            index,
            mediaId: slide.getAttribute('data-media-id'),
            src,
            fileName,
            parsed,
            isOptionLinked: fileName.includes('__'),
            galleryType: 'media-gallery'
          };
        });

        return;
      }

      const productThumbs = thumbSlider ? thumbSlider.querySelectorAll('.product__thumb[data-id]:not([data-cdn-thumb])') : [];
      this.galleryImages = Array.from(productThumbs).map((thumb, index) => {
        const img = thumb.querySelector('img');
        const src = img?.currentSrc || img?.src || thumb.getAttribute('href') || '';
        const parsed = this.parseGalleryImageFilename(src);
        const fileWithQuery = src.split('/').pop() || '';
        const fileName = fileWithQuery.split('?')[0];

        return {
          index,
          mediaId: thumb.getAttribute('data-id'),
          src,
          fileName,
          parsed,
          isOptionLinked: fileName.includes('__'),
          galleryType: 'product-images'
        };
      });
    }

    mapControlledByData(controlledByString = "") {
      const map = {};

      const variants = controlledByString.split(",");
      for (let i = 0; i < variants.length; i++) {
        const variantData = variants[i];
        const variantDataSplit = variantData.split(":");

        const variantId = variantDataSplit[0];
        const amountOfTimesToShowOptions = variantDataSplit[variantDataSplit.length - 1];

        map[variantId] = amountOfTimesToShowOptions;
      }

      return map;
    }

    findProductFromVariantId(variantId) {
      let product = null;

      for (let i = 0; i < this.products.length; i++) {
        const p = this.products[i];
        const variants = p.variants;

        let foundVariantId = false;
        for (let v = 0; v < variants.length; v++) {
          const variant = variants[v];

          if (variant.id == variantId) {
            foundVariantId = true;
            break;
          }
        }

        if (foundVariantId) {
          product = p;
          break;
        }
      }

      return product;
    }

    findProductById(productId) {
      for (let i = 0; i < this.products.length; i++) {
        const product = this.products[i];

        if (String(product.id) === String(productId)) {
            return product;
        }
      }

      return null;
    }

    normalizeOptionToken(value = '') {
      return String(value)
        .trim()
        .toLowerCase()
        .replace(/\.[^.]+$/, '')
        .replace(/_[0-9]+x[0-9]+(?=$)/, '')
        .replace(/_[0-9]+x(?=$)/, '')
        .replace(/[^a-z0-9_]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    }

    parseGalleryImageFilename(src = '') {
      if (!src) {
        return null;
      }

      const fileWithQuery = src.split('/').pop() || '';
      const fileName = fileWithQuery.split('?')[0];
      if (!fileName) {
        return null;
      }

      const fileNameWithoutExtension = fileName.replace(/\.[^.]+$/, '');
      const sizeNormalizedFileName = fileNameWithoutExtension
        .replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|original)$/, '')
        .replace(/_[0-9]+x[0-9]+$/, '')
        .replace(/_[0-9]+x$/, '')
        .replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
      const { baseFileName, angle } = this.extractImageAngleSuffix(sizeNormalizedFileName);
      const segments = baseFileName.split('__').filter(Boolean);

      if (!segments.length) {
        return null;
      }

      const sku = segments[0].trim().toLowerCase();
      if (!sku) {
        return null;
      }

      const conditions = [];
      let lastCondition = null;

      for (let i = 1; i < segments.length; i++) {
        const segment = segments[i].trim();
        if (!segment) {
          continue;
        }
        
        if (segment.includes('--')) {
          const [rawKey, ...rawValueParts] = segment.split('--');
          const rawValue = rawValueParts.join('--');
          const condition = {
            key: this.normalizeOptionToken(rawKey).replace(/-/g, '_'),
            value: this.normalizeOptionToken(rawValue),
            repeat: ''
          };

          conditions.push(condition);
          lastCondition = condition;
          continue;
        }

        if (lastCondition && !lastCondition.repeat) {
          lastCondition.repeat = this.normalizeOptionToken(segment);
        }
      }

      return {
        sku,
        conditions,
        angle
      };
    }

    extractImageAngleSuffix(baseFileName = '') {
      const suffixPattern = new RegExp(`_(${this.constructor.IMAGE_ANGLE_SUFFIXES.join('|')})$`, 'i');
      const angleMatch = baseFileName.match(suffixPattern);

      if (!angleMatch) {
        return {
          baseFileName,
          angle: ''
        };
      }

      return {
        baseFileName: baseFileName.replace(suffixPattern, ''),
        angle: this.normalizeOptionToken(angleMatch[1])
      };
    }

    getImageAnglePriority(angle = '') {
      const normalizedAngle = this.normalizeOptionToken(angle);
      const priorityMap = {
        '': 0,
        front: 1,
        back: 2,
        side: 3,
        detail: 4
      };

      return typeof priorityMap[normalizedAngle] === 'number'
        ? priorityMap[normalizedAngle]
        : 99;
    }

    // Trace logging is disabled. Leave the call sites in place so they can be
    // re-enabled by flipping cdnLog to console.log if needed for debugging.
    cdnLog() {}

    cdnWarn(...args) {
      console.warn('[Custom Variant Picker]', ...args);
    }

    getCdnCacheQueryString() {
      // Sourced from settings.cdn_cache_version via a data attribute on
      // <custom-variant-picker>. Bumping the theme setting busts the CDN cache
      // for both the manifest and the image URLs.
      const raw = this.getAttribute('data-cdn-cache-version') || '';
      const version = raw.trim();
      return version ? `?v=${encodeURIComponent(version)}` : '';
    }

    stripSkuSuffix(sku) {
      // Shopify SKUs like "91308CUW-A" or "91308CUW-B" map to a shared CDN
      // folder "91308CUW". Strip the last hyphenated segment (e.g. "-A") to
      // produce the base SKU used on the CDN.
      const dashIndex = sku.lastIndexOf('-');
      if (dashIndex > 0) {
        return sku.substring(0, dashIndex);
      }
      return '';
    }

    async fetchCdnManifest(skuFolder) {
      // Attempt to load index.json for a single SKU folder. Returns the parsed
      // manifest object on success, or null on 404 / network error.
      const cacheQuery = this.getCdnCacheQueryString();
      const baseUrl = `${this.constructor.CDN_BASE_URL}/${encodeURIComponent(skuFolder)}`;
      const manifestUrl = `${baseUrl}/index.json${cacheQuery}`;
      this.cdnLog('fetchCdnManifest: trying', manifestUrl);

      try {
        const response = await fetch(manifestUrl, { cache: 'default' });
        this.cdnLog('fetchCdnManifest: status', response.status, response.statusText);
        if (!response.ok) {
          return null;
        }
        const manifest = await response.json();
        const images = Array.isArray(manifest?.images) ? manifest.images : [];
        if (!images.length) {
          return null;
        }
        return { manifest, images, baseUrl, cacheQuery };
      } catch (error) {
        this.cdnWarn('fetchCdnManifest: fetch/parse failed', manifestUrl, error);
        return null;
      }
    }

    async loadCdnGalleryImages() {
      this.cdnLog('loadCdnGalleryImages() start');

      const baseVariant = this.getBaseSelectedVariant();
      if (!baseVariant) {
        this.cdnWarn('abort: getBaseSelectedVariant() returned null. products.length=', this.products.length);
        this.cdnImages = [];
        return;
      }
      if (!baseVariant.sku) {
        this.cdnWarn('abort: base variant has no sku', baseVariant);
        this.cdnImages = [];
        return;
      }

      const skuForUrl = String(baseVariant.sku).trim();
      this.cdnLog('base variant sku (raw):', baseVariant.sku, '-> url sku:', skuForUrl);
      if (!skuForUrl) {
        this.cdnWarn('abort: empty sku after trim');
        this.cdnImages = [];
        return;
      }

      // 1) Try the exact Shopify SKU first (e.g. "91308CUW-A").
      let result = await this.fetchCdnManifest(skuForUrl);

      // 2) If not found, strip the last -suffix and retry with the base SKU
      //    (e.g. "91308CUW"). Shopify often appends -A/-B per variant while the
      //    CDN folder uses the shared root.
      if (!result) {
        const baseSku = this.stripSkuSuffix(skuForUrl);
        if (baseSku && baseSku !== skuForUrl) {
          this.cdnLog('loadCdnGalleryImages: exact SKU not found, retrying with base SKU:', baseSku);
          result = await this.fetchCdnManifest(baseSku);
        }
      }

      if (!result) {
        this.cdnImages = [];
        return;
      }

      const { images, baseUrl, cacheQuery } = result;
      this.cdnLog('manifest parsed:', { imageCount: images.length });

      let rejectedCount = 0;
      const rejectedSamples = [];
      this.cdnImages = images
        .map((fileName) => {
          if (typeof fileName !== 'string' || !fileName) {
            rejectedCount += 1;
            if (rejectedSamples.length < 3) rejectedSamples.push({ fileName, reason: 'not-a-string' });
            return null;
          }
          const parsed = this.parseGalleryImageFilename(fileName);
          if (!parsed || !parsed.conditions.length) {
            rejectedCount += 1;
            if (rejectedSamples.length < 3) rejectedSamples.push({ fileName, reason: 'no-conditions', parsed });
            return null;
          }
          return {
            fileName,
            src: `${baseUrl}/${fileName}${cacheQuery}`,
            parsed
          };
        })
        .filter(Boolean);

      this.cdnLog(
        'parsed CDN images:', this.cdnImages.length,
        '/ rejected:', rejectedCount
      );
      if (rejectedCount) {
        this.cdnLog('rejected samples:', rejectedSamples);
      }
      if (this.cdnImages.length) {
        this.cdnLog('first parsed entry:', this.cdnImages[0]);
      }

      if (!this.cdnImages.length) {
        this.cdnWarn('every filename was rejected — nothing to match against');
        return;
      }

      const slot = this.ensureCdnGallerySlot();
      this.cdnLog('ensureCdnGallerySlot() ->', slot ? 'slot ready' : 'slot NOT created');
      this.syncGalleryImage();
    }

    ensureCdnGallerySlot() {
      const { mediaGalleryEl, mainSlider, thumbSlider } = this.getScopedGalleryEls();

      if (mediaGalleryEl) {
        return this.ensureCdnGallerySlotForMediaGallery(mediaGalleryEl);
      }

      if (mainSlider) {
        return this.ensureCdnGallerySlotForProductImages(mainSlider, thumbSlider);
      }

      this.cdnWarn('ensureCdnGallerySlot: no <media-gallery> AND no [data-product-photos] — cannot create a CDN slot');
      return null;
    }

    ensureCdnGallerySlotForMediaGallery(mediaGalleryEl) {
      let slide = mediaGalleryEl.querySelector('[data-cdn-slide]');
      if (slide) {
        this.cdnLog('ensureCdnGallerySlot[media-gallery]: reusing existing slot');
        return slide;
      }

      const mainContainer = mediaGalleryEl.querySelector('.media-gallery__main');
      if (!mainContainer) {
        this.cdnWarn('ensureCdnGallerySlot[media-gallery]: no .media-gallery__main container');
        return null;
      }
      this.cdnLog('ensureCdnGallerySlot[media-gallery]: creating new slot + thumb');

      slide = document.createElement('div');
      slide.className = 'media-gallery__slide hide';
      slide.setAttribute('data-media-id', 'cdn-slot');
      slide.setAttribute('data-media-type', 'image');
      slide.setAttribute('data-cdn-slide', '');

      const slideImg = document.createElement('img');
      slideImg.alt = '';
      slideImg.loading = 'lazy';
      slide.appendChild(slideImg);

      mainContainer.appendChild(slide);

      const thumbnails = mediaGalleryEl.querySelector('.media-gallery__thumbnails');
      if (thumbnails) {
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'media-gallery__thumb hide';
        thumb.setAttribute('data-media-id', 'cdn-slot');
        thumb.setAttribute('data-media-type', 'image');
        thumb.setAttribute('data-cdn-thumb', '');
        thumb.setAttribute('aria-label', 'View selected variant image');

        const thumbImageWrapper = document.createElement('div');
        thumbImageWrapper.className = 'media-gallery__thumb-image';
        const thumbImg = document.createElement('img');
        thumbImg.alt = '';
        thumbImg.loading = 'lazy';
        thumbImageWrapper.appendChild(thumbImg);
        thumb.appendChild(thumbImageWrapper);
        thumbnails.appendChild(thumb);

        thumb.addEventListener('click', () => {
          if (typeof mediaGalleryEl.goToSlide !== 'function') {
            return;
          }
          const slides = Array.from(mediaGalleryEl.querySelectorAll('.media-gallery__slide'));
          const slideIndex = slides.indexOf(slide);
          if (slideIndex < 0) {
            return;
          }
          const direction = slideIndex > (mediaGalleryEl.currentIndex || 0) ? 'next' : 'prev';
          mediaGalleryEl.goToSlide(slideIndex, direction);
        });
      }

      // Refresh the custom element's cached slide/thumb NodeLists so the new
      // slot participates in keyboard/swipe navigation.
      mediaGalleryEl.slides = mediaGalleryEl.querySelectorAll('.media-gallery__slide');
      mediaGalleryEl.thumbs = mediaGalleryEl.querySelectorAll('.media-gallery__thumb');

      return slide;
    }

    ensureCdnGallerySlotForProductImages(mainSlider, thumbSlider) {
      // When Flickity initializes, it REPARENTS all .product-main-slide cells
      // out of [data-product-photos] and into its own .flickity-slider viewport
      // wrapper. We have to look for the slot in both locations (and for the
      // template slide we're about to clone) because a querySelector scoped to
      // mainSlider will find cells in both places, but a raw appendChild will
      // put new cells outside the Flickity wrapper where Flickity can't see
      // them.
      let slide = mainSlider.querySelector('[data-cdn-slide]');
      if (slide) {
        this.cdnLog('ensureCdnGallerySlot[product-images]: reusing existing slot');
        return slide;
      }

      this.cdnLog('ensureCdnGallerySlot[product-images]: creating new slot');

      const flickity = (window.Flickity && typeof window.Flickity.data === 'function')
        ? window.Flickity.data(mainSlider)
        : null;
      const isFlickityReady = !!(flickity && typeof flickity.append === 'function');
      this.cdnLog('ensureCdnGallerySlot[product-images]: flickity ready?', isFlickityReady);

      // Clone an existing slide to inherit the image-wrap aspect-ratio hack +
      // whatever classes the theme has applied. This keeps the Flickity cell
      // the right height and prevents layout jumps. Once Flickity is up, the
      // cells live inside .flickity-slider, so query on mainSlider (which
      // matches both locations) rather than a direct child selector.
      const templateSlide = mainSlider.querySelector('.product-main-slide');
      const cdnIndex = this.getCdnSlotIndexValue();

      if (templateSlide) {
        slide = templateSlide.cloneNode(true);
        // Strip anything the template brought with it that we don't want.
        slide.classList.remove('starting-slide', 'is-selected');
        slide.classList.add('secondary-slide', 'hide');
        // Flickity adds positioning styles to its cells — clear them so the
        // new cell gets repositioned when reloadCells/select runs.
        slide.style.position = '';
        slide.style.left = '';
        slide.setAttribute('data-index', String(cdnIndex));
        slide.setAttribute('data-cdn-slide', '');

        const imageWrap = slide.querySelector('.image-wrap');
        if (imageWrap) {
          imageWrap.innerHTML = '<img alt="" loading="lazy">';
        } else {
          // Fallback if cloned structure is missing the wrap
          slide.innerHTML = '<div class="product-image-main"><div class="image-wrap"><img alt="" loading="lazy"></div></div>';
        }
      } else {
        // Empty slider or unexpected markup — build a minimal slide.
        slide = document.createElement('div');
        slide.className = 'product-main-slide secondary-slide hide';
        slide.setAttribute('data-index', String(cdnIndex));
        slide.setAttribute('data-cdn-slide', '');
        slide.innerHTML = '<div class="product-image-main"><div class="image-wrap"><img alt="" loading="lazy"></div></div>';
      }

      if (isFlickityReady) {
        // flickity.append() handles the DOM reparenting into .flickity-slider
        // AND updates the internal .cells array. Using raw appendChild here
        // would put the slide OUTSIDE .flickity-slider and Flickity would
        // never see it.
        try {
          flickity.append(slide);
          if (typeof flickity.resize === 'function') {
            flickity.resize();
          }
          this.cdnLog('ensureCdnGallerySlot[product-images]: flickity.append() done. cell count:', flickity.cells?.length);
        } catch (error) {
          this.cdnWarn('ensureCdnGallerySlot[product-images]: flickity.append failed', error);
        }
      } else {
        // Flickity not up yet — append to the raw container. When Flickity
        // initializes later, it will pick this slide up as part of its initial
        // cell collection.
        mainSlider.appendChild(slide);
        this.cdnLog('ensureCdnGallerySlot[product-images]: Flickity not ready; appended directly to mainSlider');
      }

      // Matching thumb, so the existing updateGalleryVisibility/syncGalleryImage
      // plumbing (which keys off thumb data-id) can find it.
      if (thumbSlider) {
        const scroller = thumbSlider.querySelector('.product__thumbs--scroller') || thumbSlider;
        const thumbItem = document.createElement('div');
        thumbItem.className = 'product__thumb-item hide';
        thumbItem.setAttribute('data-index', String(cdnIndex));

        const thumb = document.createElement('a');
        thumb.href = '#';
        thumb.className = 'product__thumb';
        thumb.setAttribute('data-product-thumb', '');
        thumb.setAttribute('data-cdn-thumb', '');
        thumb.setAttribute('data-id', 'cdn-slot');
        thumb.setAttribute('data-index', String(cdnIndex));
        thumb.innerHTML = '<div class="image-wrap image-wrap__thumbnail" style="height:0;padding-bottom:100%;"><img alt="" loading="lazy"></div>';

        thumbItem.appendChild(thumb);
        scroller.appendChild(thumbItem);

        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectCdnSlideInProductImages();
        });
      }

      return slide;
    }

    getCdnSlotIndexValue() {
      // Pick an index past any existing ones so we don't collide with the
      // Shopify slides' data-index values.
      const { mainSlider } = this.getScopedGalleryEls();
      if (!mainSlider) {
        return 0;
      }

      const slides = mainSlider.querySelectorAll('.product-main-slide[data-index]');
      let max = -1;
      slides.forEach((el) => {
        const n = parseInt(el.getAttribute('data-index'), 10);
        if (Number.isInteger(n) && n > max) {
          max = n;
        }
      });

      return max + 1;
    }

    getProductImagesFlickityCellIndex(mainSlider, slideOrThumbItem) {
      if (!mainSlider || !slideOrThumbItem || !window.Flickity || typeof window.Flickity.data !== 'function') {
        return -1;
      }

      const flickity = window.Flickity.data(mainSlider);
      if (!flickity || !Array.isArray(flickity.cells)) {
        return -1;
      }

      let slide = slideOrThumbItem;
      if (!slide.classList.contains('product-main-slide')) {
        const slideIndex = slide.getAttribute('data-index');
        slide = mainSlider.querySelector(`.product-main-slide[data-index="${slideIndex}"]`);
      }

      if (!slide) {
        return -1;
      }

      for (let i = 0; i < flickity.cells.length; i++) {
        if (flickity.cells[i].element === slide) {
          return i;
        }
      }

      return -1;
    }

    selectCdnSlideInProductImages() {
      // Alias kept for the thumb click handler; routes through the same logic.
      this.activateCdnSlideInProductImages();
    }

    activateCdnSlideInProductImages() {
      const { mainSlider } = this.getScopedGalleryEls();
      if (!mainSlider) {
        this.cdnWarn('activateCdnSlideInProductImages: no [data-product-photos]');
        return;
      }
      const slide = mainSlider.querySelector('[data-cdn-slide]');
      if (!slide) {
        this.cdnWarn('activateCdnSlideInProductImages: no CDN slide in DOM');
        return;
      }
      // Make sure the cell is visible before asking Flickity to select it.
      slide.classList.remove('hide');

      if (!window.Flickity || typeof window.Flickity.data !== 'function') {
        this.cdnWarn('activateCdnSlideInProductImages: window.Flickity unavailable');
        return;
      }
      const flickity = window.Flickity.data(mainSlider);
      if (!flickity || typeof flickity.select !== 'function') {
        this.cdnWarn('activateCdnSlideInProductImages: Flickity not initialized on mainSlider');
        return;
      }

      // reloadCells() is idempotent and re-scans child .product-main-slide
      // elements; call it first so a freshly injected slide is part of the
      // cell list before we ask Flickity to pick it.
      if (typeof flickity.reloadCells === 'function') {
        flickity.reloadCells();
      }
      if (typeof flickity.resize === 'function') {
        flickity.resize();
      }

      // Prefer Flickity's own cell index (.cells is the authoritative list
      // post-reloadCells) over a DOM indexOf, since Flickity may reorder with
      // wrapAround.
      let cellIndex = -1;
      if (Array.isArray(flickity.cells)) {
        for (let i = 0; i < flickity.cells.length; i++) {
          if (flickity.cells[i].element === slide) {
            cellIndex = i;
            break;
          }
        }
      }
      if (cellIndex < 0) {
        const cells = Array.from(mainSlider.querySelectorAll('.product-main-slide'));
        cellIndex = cells.indexOf(slide);
      }

      if (cellIndex < 0) {
        this.cdnWarn('activateCdnSlideInProductImages: CDN slide not found in Flickity cells or DOM');
        return;
      }

      this.cdnLog('activateCdnSlideInProductImages: flickity.select(', cellIndex, ')');
      flickity.select(cellIndex, false, true);
    }

    findBestCdnMatch() {
      if (!this.cdnImages || !this.cdnImages.length) {
        this.cdnLog('findBestCdnMatch: no cdnImages loaded yet');
        return null;
      }

      const baseVariant = this.getBaseSelectedVariant();
      if (!baseVariant || !baseVariant.sku) {
        this.cdnWarn('findBestCdnMatch: no base variant / sku');
        return null;
      }

      const sku = this.normalizeOptionToken(baseVariant.sku);
      // Build a set of SKU values to accept. When the Shopify SKU has a
      // -suffix (e.g. "91308CUW-A"), the CDN manifest filenames use the
      // shared base (e.g. "91308CUW"), so we accept both the full and the
      // stripped version to ensure matching works.
      const acceptableSkus = new Set([sku]);
      const strippedSku = this.normalizeOptionToken(this.stripSkuSuffix(String(baseVariant.sku).trim()));
      if (strippedSku && strippedSku !== sku) {
        acceptableSkus.add(strippedSku);
      }
      const selectedOptions = this.getSelectedImageOptions();
      this.cdnLog('findBestCdnMatch: acceptable skus =', Array.from(acceptableSkus), ' selectedOptions =', selectedOptions);

      let bestMatch = null;
      let skuFilteredOut = 0;
      let zeroMatchCount = 0;
      const topCandidates = [];

      for (let i = 0; i < this.cdnImages.length; i++) {
        const cdnImage = this.cdnImages[i];
        const parsed = cdnImage.parsed;

        if (!parsed || !acceptableSkus.has(parsed.sku) || !parsed.conditions.length) {
          skuFilteredOut += 1;
          continue;
        }

        let matchedConditions = 0;
        let unmatchedConditions = 0;

        for (let j = 0; j < parsed.conditions.length; j++) {
          const condition = parsed.conditions[j];
          const selections = selectedOptions[condition.key] || [];

          if (this.doesImageConditionMatchSelection(condition, selections)) {
            matchedConditions += 1;
          } else {
            unmatchedConditions += 1;
          }
        }

        if (matchedConditions <= 0) {
          zeroMatchCount += 1;
          continue;
        }

        const isFullMatch = unmatchedConditions === 0;
        const candidate = {
          ...cdnImage,
          score: matchedConditions,
          isFullMatch,
          matchType: isFullMatch ? 'full' : 'partial',
          matchedConditions,
          unmatchedConditions,
          conditionCount: parsed.conditions.length,
          angle: parsed.angle || ''
        };

        topCandidates.push({
          fileName: candidate.fileName,
          score: candidate.score,
          matchType: candidate.matchType,
          matched: candidate.matchedConditions,
          total: candidate.conditionCount
        });

        if (
          !bestMatch ||
          candidate.score > bestMatch.score ||
          (candidate.score === bestMatch.score && isFullMatch && !bestMatch.isFullMatch) ||
          (candidate.score === bestMatch.score && isFullMatch === bestMatch.isFullMatch && candidate.conditionCount > bestMatch.conditionCount) ||
          (
            candidate.score === bestMatch.score &&
            isFullMatch === bestMatch.isFullMatch &&
            candidate.conditionCount === bestMatch.conditionCount &&
            this.getImageAnglePriority(candidate.angle) < this.getImageAnglePriority(bestMatch.angle)
          )
        ) {
          bestMatch = candidate;
        }
      }

      // Keep a sample so the console isn't drowned on 1k-image SKUs
      const candidatePreview = topCandidates
        .sort((a, b) => b.score - a.score || b.total - a.total)
        .slice(0, 5);

      this.cdnLog('findBestCdnMatch: scan complete', {
        total: this.cdnImages.length,
        skuOrConditionFilteredOut: skuFilteredOut,
        zeroMatch: zeroMatchCount,
        scored: topCandidates.length,
        topCandidates: candidatePreview,
        bestMatch: bestMatch
          ? {
            fileName: bestMatch.fileName,
            matchType: bestMatch.matchType,
            score: `${bestMatch.matchedConditions}/${bestMatch.conditionCount}`,
            src: bestMatch.src
          }
          : null
      });

      // If there is no match at all and the first condition keys in the
      // manifest don't appear in selectedOptions, that's usually the bug.
      if (!bestMatch && this.cdnImages.length) {
        const manifestKeys = new Set();
        for (let i = 0; i < Math.min(this.cdnImages.length, 20); i++) {
          const conditions = this.cdnImages[i].parsed?.conditions || [];
          for (let j = 0; j < conditions.length; j++) {
            manifestKeys.add(conditions[j].key);
          }
        }
        const selectedKeys = new Set(Object.keys(selectedOptions));
        const manifestKeysList = Array.from(manifestKeys);
        const missingFromSelection = manifestKeysList.filter((k) => !selectedKeys.has(k));
        if (missingFromSelection.length) {
          this.cdnWarn(
            'findBestCdnMatch: no match because these manifest option keys are not in selectedOptions:',
            missingFromSelection,
            '(manifest keys:', manifestKeysList,
            '/ selected keys:', Array.from(selectedKeys), ')'
          );
        }
      }

      return bestMatch;
    }

    updateCdnGallerySlot(bestCdnMatch) {
      const { mediaGalleryEl, mainSlider } = this.getScopedGalleryEls();

      if (mediaGalleryEl) {
        return this.updateCdnGallerySlotForMediaGallery(
          mediaGalleryEl,
          bestCdnMatch
        );
      }

      if (mainSlider) {
        return this.updateCdnGallerySlotForProductImages(
          mainSlider,
          bestCdnMatch
        );
      }

      this.cdnWarn('updateCdnGallerySlot: no gallery container on page');
      return null;
    }

    updateCdnGallerySlotForMediaGallery(mediaGalleryEl, bestCdnMatch) {
      const slide = mediaGalleryEl.querySelector('[data-cdn-slide]');
      if (!slide) {
        this.cdnWarn('updateCdnGallerySlot[media-gallery]: slide element missing');
        return null;
      }
      const thumb = mediaGalleryEl.querySelector('[data-cdn-thumb]');

      if (!bestCdnMatch) {
        this.cdnLog('updateCdnGallerySlot[media-gallery]: no match -> hiding slot');
        slide.classList.add('hide');
        if (thumb) thumb.classList.add('hide');
        return null;
      }

      const slideImg = slide.querySelector('img');
      if (slideImg && slideImg.getAttribute('src') !== bestCdnMatch.src) {
        this.cdnLog('updateCdnGallerySlot[media-gallery]: swapping slide src ->', bestCdnMatch.src);
        slideImg.setAttribute('src', bestCdnMatch.src);
        slideImg.alt = bestCdnMatch.fileName;
      }
      slide.classList.remove('hide');

      if (thumb) {
        const thumbImg = thumb.querySelector('img');
        if (thumbImg && thumbImg.getAttribute('src') !== bestCdnMatch.src) {
          thumbImg.setAttribute('src', bestCdnMatch.src);
          thumbImg.alt = bestCdnMatch.fileName;
        }
        thumb.classList.remove('hide');
      }

      const slides = Array.from(mediaGalleryEl.querySelectorAll('.media-gallery__slide'));
      const slotIndex = slides.indexOf(slide);

      return {
        ...bestCdnMatch,
        index: slotIndex,
        mediaId: 'cdn-slot',
        galleryType: 'media-gallery',
        isCdn: true
      };
    }

    updateCdnGallerySlotForProductImages(mainSlider, bestCdnMatch) {
      const slide = mainSlider.querySelector('[data-cdn-slide]');
      if (!slide) {
        this.cdnWarn('updateCdnGallerySlot[product-images]: slide element missing');
        return null;
      }
      const { thumbSlider } = this.getScopedGalleryEls();
      const thumb = thumbSlider?.querySelector('[data-cdn-thumb]');
      const thumbItem = thumb?.closest('.product__thumb-item');

      if (!bestCdnMatch) {
        this.cdnLog('updateCdnGallerySlot[product-images]: no match -> hiding slot');
        slide.classList.add('hide');
        if (thumbItem) thumbItem.classList.add('hide');
        if (thumb) thumb.classList.add('hide');
        return null;
      }

      const slideImg = slide.querySelector('img');
      if (slideImg && slideImg.getAttribute('src') !== bestCdnMatch.src) {
        this.cdnLog('updateCdnGallerySlot[product-images]: swapping slide src ->', bestCdnMatch.src);
        slideImg.setAttribute('src', bestCdnMatch.src);
        slideImg.alt = bestCdnMatch.fileName;
      }
      slide.classList.remove('hide');

      if (thumb) {
        const thumbImg = thumb.querySelector('img');
        if (thumbImg && thumbImg.getAttribute('src') !== bestCdnMatch.src) {
          thumbImg.setAttribute('src', bestCdnMatch.src);
          thumbImg.alt = bestCdnMatch.fileName;
        }
        thumb.classList.remove('hide');
      }
      if (thumbItem) thumbItem.classList.remove('hide');

      const cdnIndex = parseInt(slide.getAttribute('data-index'), 10);
      const slotIndex = Number.isInteger(cdnIndex) ? cdnIndex : -1;

      this.cdnLog('updateCdnGallerySlot[product-images]: slot visible at data-index', slotIndex);

      return {
        ...bestCdnMatch,
        index: slotIndex,
        mediaId: 'cdn-slot',
        galleryType: 'product-images',
        isCdn: true
      };
    }

    loadOptionsUI() {
      const optionWrappers = this.querySelectorAll('.option-wrapper');
      const processedUI = [];
      for (let i = 0; i < optionWrappers.length; i++) {
        const optWrapper = optionWrappers[i];
        const controlledByRaw = optWrapper.getAttribute('data-option-controlled-by');
        const productOptionFor = optWrapper.getAttribute('data-product-option-for');

        if (controlledByRaw != null && controlledByRaw != "" && productOptionFor != "") {
          const processedKey = productOptionFor + "||" + controlledByRaw;

          if (!processedUI.includes(processedKey)) {
            const controlledByMap = this.mapControlledByData(controlledByRaw);
            const controlledByMapKeys = Object.keys(controlledByMap);

            if (controlledByMapKeys.length <= 0) {
              console.warn("[Custom Product Options] Cannot process the current option. Controlled by map was not defined!");
              continue;
            }

            if ( controlledByMapKeys.length == 1 ) {
              console.warn("[Custom Product Options] Cannot process the current option. Missing variant id in depends on map.");
              console.log( "[Custom Product Options] Related Option: " , optWrapper );
              continue;
            }

            const firstVariantId = controlledByMapKeys[0];
            const productRelatedToFirstVariantInMap = this.findProductFromVariantId(firstVariantId);

            if (productRelatedToFirstVariantInMap === null) {
              console.warn("[Custom Product Options] The variant id in the current map could not be found in product JSON for all products on the page.");
              continue;
            }

            const productIdFromRelatedProduct = productRelatedToFirstVariantInMap.id;
            const overallSelectForRelatedProduct = this.querySelector(`[data-product-option-for="${productIdFromRelatedProduct}"] select.raw-data`);

            // which value is set?
            const selectedOption = overallSelectForRelatedProduct.options[overallSelectForRelatedProduct.selectedIndex];
            const selectedVariantId = selectedOption.value;

            const showOptionsCount = controlledByMap[selectedVariantId];
            const elementsToProcess = this.querySelectorAll(`[data-product-option-for="${productOptionFor}"][data-option-controlled-by="${controlledByRaw}"]`);
            for (let i = 0; i < elementsToProcess.length; i++) {
              elementsToProcess[i].classList.add('hidden');

              const currentElCount = i + 1;
              if (currentElCount <= showOptionsCount) {
                elementsToProcess[i].classList.remove('hidden');
              }
            }

            processedUI.push(processedKey);
          }
        }

      }

      // for each wrapper on the page
      // does it have a data-option-controlled-by value?
      // if yes
      // have we already processed this data set?
      // grab the variant data from the data
      // split at , for list of variants:show options count
      // split at the : and load into a lookup table where the key is the variant id and the value is the count

      // now using the first variant id in the list find the related product
      // using that related product, we want to check the UI for the value currently set for that product
      // whatever that selected value is will tell us the variant id we should use
      // the variant id selected will tell us show options count
      // now select all of the data-option-controlled-by matching our original extracted value
      // hide them all
      // remove hidden from a total of show options count break when done
      // add data-option-controlled-by to processed list
    }

    initChangeEvents() {
      const productType = (this.getAttribute("data-product-type") || "").trim().toUpperCase();

      this.checkboxOptions = this.querySelectorAll('.option-wrapper.checkbox .option-item .checkbox-input');
      this.dropdownOptions = this.querySelectorAll('.option-wrapper.dropdown .option-item .option-selector');
      this.colorOptions = this.querySelectorAll('.option-wrapper.color-palette .option-item .swatch-input');
      this.pillOptions = this.querySelectorAll('.option-wrapper.pills .pill-input, .option-wrapper.pills-with-images .pill-input');

      if ( productType === "BASIC" ) {
        for (let i = 0; i < this.checkboxOptions.length; i++) {
          const opt = this.checkboxOptions[i]; 
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handleBasicCheckboxOptionChange.bind(this));
        }

        for (let i = 0; i < this.dropdownOptions.length; i++) {
          const opt = this.dropdownOptions[i];
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handleBasicDropdownOptionChange.bind(this));
        }

        for (let i = 0; i < this.colorOptions.length; i++) {
          const opt = this.colorOptions[i];
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handleBasicColorOptionChange.bind(this));
        }

        for (let i = 0; i < this.pillOptions.length; i++) {
          const opt = this.pillOptions[i];
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handleBasicPillOptionChange.bind(this));
        }
      } else {
        for (let i = 0; i < this.checkboxOptions.length; i++) {
          const opt = this.checkboxOptions[i];
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handleCheckboxOptionChange.bind(this));
        }

        for (let i = 0; i < this.dropdownOptions.length; i++) {
          const opt = this.dropdownOptions[i];
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handleDropdownOptionChange.bind(this));
        }

        for (let i = 0; i < this.colorOptions.length; i++) {
          const opt = this.colorOptions[i];
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handleColorOptionChange.bind(this));
        }

        for (let i = 0; i < this.pillOptions.length; i++) {
          const opt = this.pillOptions[i];
          if (this.boundOptionInputs.has(opt)) continue;
          this.boundOptionInputs.add(opt);
          opt.addEventListener("change", this.handlePillOptionChange.bind(this));
        }
      }
    }

    handleCheckboxOptionChange(e) {
      const checkbox = e.target;
      const isChecked = checkbox.checked;

      const wrapperEl = checkbox.closest("[data-product-option-for]");
      if (!wrapperEl) {
        return;
      }

      const productId = wrapperEl.getAttribute("data-product-option-for");
      if (!productId) {
        return;
      }

      const combinedOptions = this.getAdvancedCombinedOptions(productId);
      if (!combinedOptions) {
        return;
      }

      const selectElToChange = this.getAdvancedRawSelect(productId, wrapperEl);
      if (!selectElToChange) {
        return;
      }

      let matchingOption = null;
      for (let i = 0; i < selectElToChange.options.length; i++) {
        const option = selectElToChange.options[i];

        if (option.text.trim().toLowerCase() === combinedOptions.toLowerCase()) {
          matchingOption = option;
          break;
        }
      }

      if (matchingOption != null) {
        selectElToChange.value = matchingOption.value;
        selectElToChange.dispatchEvent(
          new Event("change", { bubbles: true })
        );

        this.updateSpecificationTable(productId, matchingOption.value);
        // this.updatePrice( productId, matchingOption.value );
      }

      const formToShow = wrapperEl.querySelector(".form");
      if (formToShow != null) {
        if (isChecked) {
          formToShow.classList.remove("hidden");
        } else {
          formToShow.classList.add("hidden");
        }
      }

      this.loadOptionsUI();
      this.updatePriceV2();
      this.updateCalculatedSku();
      this.syncGalleryImage();
      this.recalculateCollapsibleHeight(wrapperEl);
      this.updateSelectionUrl();
    }

    handleDropdownOptionChange(e) {
      const selectChanged = e.target;
      const wrapperEl = selectChanged.closest("[data-product-option-for]");

      if (!wrapperEl) {
        return;
      }

      const productId = wrapperEl.getAttribute("data-product-option-for");
      if (!productId) {
        return;
      }

      const combinedOptions = this.getAdvancedCombinedOptions(productId);
      if (!combinedOptions) {
        return;
      }

      const selectElToChange = this.getAdvancedRawSelect(productId, wrapperEl);
      if (!selectElToChange) {
        return;
      }

      let matchingOption = null;
      for (let i = 0; i < selectElToChange.options.length; i++) {
        const option = selectElToChange.options[i];

        if (option.text.trim().toLowerCase() === combinedOptions.toLowerCase()) {
          matchingOption = option;
          break;
        }
      }

      if (matchingOption != null) {
        selectElToChange.value = matchingOption.value;
        selectElToChange.dispatchEvent(
          new Event("change", { bubbles: true })
        );

        this.updateSpecificationTable(productId, matchingOption.value);
        // this.updatePrice( productId, matchingOption.value );
      }

      this.loadOptionsUI();
      this.updatePriceV2();
      this.updateCalculatedSku();
      this.syncGalleryImage();
      this.recalculateCollapsibleHeight(wrapperEl);
      this.updateSelectionUrl();
    }

    handleColorOptionChange(e) {
      const swatchChanged = e.target;
      const wrapperEl = swatchChanged.closest('[data-product-option-for]');

      if (!wrapperEl) {
        return;
      }

      const productId = wrapperEl.getAttribute("data-product-option-for");
      if (!productId) {
        return;
      }

      const combinedOptions = this.getAdvancedCombinedOptions(productId);
      if (!combinedOptions) {
        return;
      }

      const selectElToChange = this.getAdvancedRawSelect(productId, wrapperEl);
      if (!selectElToChange) {
        return;
      }
      
      let matchingOption = null;
      for (let i = 0; i < selectElToChange.options.length; i++) {
        const option = selectElToChange.options[i];

        if (option.text.trim().toLowerCase() === combinedOptions.toLowerCase()) {
          matchingOption = option;
          break;
        }
      }

      if (matchingOption != null) {
        selectElToChange.value = matchingOption.value;
        selectElToChange.dispatchEvent(
          new Event('change', { bubbles: true })
        );

        this.updateSpecificationTable(productId, matchingOption.value);
        // this.updatePrice( productId, matchingOption.value );
      }

      this.handleSwatchPreview(swatchChanged, wrapperEl);
      this.handleSwatchClick(swatchChanged, wrapperEl);

      this.loadOptionsUI();
      this.updatePriceV2();
      this.updateCalculatedSku();
      this.syncGalleryImage();
      this.updateSelectionUrl();
    }

    handlePillOptionChange(e) {
      const radioChanged = e.target;
      const wrapperEl = radioChanged.closest("[data-product-option-for]");

      if (!wrapperEl) {
        return;
      }

      if (!radioChanged.checked) {
        return;
      }

      const productId = wrapperEl.getAttribute("data-product-option-for");
      if (!productId) {
        return;
      }

      const combinedOptions = this.getAdvancedCombinedOptions(productId);
      if (!combinedOptions) {
        return;
      }

      const selectElToChange = this.getAdvancedRawSelect(productId, wrapperEl);
      if (!selectElToChange) {
        return;
      }

      let matchingOption = null;

      for (let i = 0; i < selectElToChange.options.length; i++) {
        const option = selectElToChange.options[i];

        if (option.text.trim().toLowerCase() === combinedOptions.toLowerCase()) {
          matchingOption = option;
          break;
        }
      }

      if (matchingOption != null) {
        selectElToChange.value = matchingOption.value;
        selectElToChange.dispatchEvent(
          new Event("change", { bubbles: true })
        );

        this.updateSpecificationTable(productId, matchingOption.value);
        // this.updatePrice( productId, matchingOption.value );
      }

      this.loadOptionsUI();
      this.updatePriceV2();
      this.updateCalculatedSku();
      this.syncGalleryImage();
      this.recalculateCollapsibleHeight(wrapperEl);
      this.updateSelectionUrl();
    }

    handleBasicCheckboxOptionChange(e) {
      const input = e.target;
      const wrapperEl = input.closest("[data-product-option-for]");

      if (!wrapperEl) {
        return;
      }

      const formToShow = wrapperEl.querySelector(".form");
      if (formToShow != null) {
        if (input.checked) {
          formToShow.classList.remove("hidden");
        } else {
          formToShow.classList.add("hidden");
        }
      }

      this.handleBasicOptionChange(wrapperEl);
    }

    handleBasicDropdownOptionChange(e) {
      const input = e.target;
      const wrapperEl = input.closest("[data-product-option-for]");

      if (!wrapperEl) {
        return;
      }

      this.handleBasicOptionChange(wrapperEl);
    }

    handleBasicColorOptionChange(e) {
      const input = e.target;
      const wrapperEl = input.closest("[data-product-option-for]");

      if (!wrapperEl) {
        return;
      }

      this.handleSwatchPreview(input);
      this.handleSwatchClick(input, wrapperEl);
      this.handleBasicOptionChange(wrapperEl);
    }

    handleBasicPillOptionChange(e) {
      const input = e.target;
      const wrapperEl = input.closest("[data-product-option-for]");

      if (!wrapperEl) {
        return;
      }

      if (!input.checked) {
        return;
      }

      this.handleBasicOptionChange(wrapperEl);
    }

    handleBasicOptionChange(wrapperEl) {
      const productOptionFor = wrapperEl.getAttribute("data-product-option-for");

      if (!productOptionFor) {
        return;
      }

      const selectElToChange = this.querySelector(
        `select.raw-data#options-for-${productOptionFor}`
      );

      if (!selectElToChange) {
        return;
      }

      const combinedOptions = this.getBasicCombinedOptions(productOptionFor);

      if (!combinedOptions) {
        return;
      }

      let matchingOption = null;

      for (let i = 0; i < selectElToChange.options.length; i++) {
        const option = selectElToChange.options[i];

        if (option.text.trim().toLowerCase() === combinedOptions.toLowerCase()) {
          matchingOption = option;
          break;
        }
      }

      if (matchingOption != null) {
        selectElToChange.value = matchingOption.value;
        selectElToChange.dispatchEvent(
          new Event("change", { bubbles: true })
        );

        this.updateSpecificationTable(productOptionFor, matchingOption.value);
        // this.updatePrice( productOptionFor, matchingOption.value );
      }

      this.loadOptionsUI();
      this.updatePriceV2();
      this.updateCalculatedSku();
      this.syncGalleryImage();
      this.recalculateCollapsibleHeight(wrapperEl);
      this.updateSelectionUrl();
    }

    getBasicCombinedOptions(productOptionFor) {
      const relatedWrappers = this.querySelectorAll(
        `[data-product-option-for="${productOptionFor}"]:not(.hidden)`
      );

      const selectedValues = [];

      for (let i = 0; i < relatedWrappers.length; i++) {
        const currentWrapper = relatedWrappers[i];

        const checkedPill = currentWrapper.querySelector(".pill-input:checked");
        if (checkedPill) {
          selectedValues.push(checkedPill.value.trim());
          continue;
        }

        const checkedSwatch = currentWrapper.querySelector(
          '.option-wrapper.color-palette .swatch-input:checked, .swatch-input:checked'
        );
        if (checkedSwatch) {
          const swatchLabel = checkedSwatch.closest(".swatch-label");
          const swatchName = swatchLabel ? swatchLabel.getAttribute("data-swatch-name") : "";

          if (swatchName) {
            selectedValues.push(swatchName.trim());
            continue;
          }

          const swatchValue =
            checkedSwatch.getAttribute("data-option-shopify-value") ||
            checkedSwatch.getAttribute("data-option-value");

          if (swatchValue) {
            selectedValues.push(swatchValue.trim());
            continue;
          }
        }

        const optionSelectors = currentWrapper.querySelectorAll(".option-selector");
        if (optionSelectors.length > 0) {
          for (let j = 0; j < optionSelectors.length; j++) {
            const selectEl = optionSelectors[j];
            const selectedOption = selectEl.options[selectEl.selectedIndex];

            if (selectedOption && selectedOption.value) {
              selectedValues.push(selectedOption.text.trim());
            }
          }

          continue;
        }

        const checkboxInput = currentWrapper.querySelector(".checkbox-input");
        if (checkboxInput) {
          if (checkboxInput.checked) {
            selectedValues.push("Yes");
          } else {
            selectedValues.push("No");
          }

          continue;
        }
      }

      return selectedValues.join(" / ");
    }

    getAdvancedCombinedOptions(productId) {
      const selectedValues = [];

      const productType = (this.getAttribute("data-product-type") || "").trim().toUpperCase();
      const wrappers = this.querySelectorAll(
        `[data-product-option-for="${productId}"]:not(.hidden):not(.additional-metafield-option)`
      );

      for (let i = 0; i < wrappers.length; i++) {
        const wrapperEl = wrappers[i];

        if (
          productType === "ADD_ONS" &&
          wrapperEl.querySelector(`:scope .option-wrapper[data-product-option-for="${productId}"]`)
        ) {
          continue;
        }

        const optionContainers = Array.from(wrapperEl.children).filter((child) => {
          return child.classList && child.classList.contains("option-container");
        });

        if (!optionContainers.length) {
          if (productType === "ADD_ONS") {
            optionContainers.push(wrapperEl);
          } else {
            continue;
          }
        }

        for (let j = 0; j < optionContainers.length; j++) {
          const containerEl = optionContainers[j];

          const checkedPill = containerEl.querySelector(".pill-input:checked");
          if (checkedPill) {
            if (checkedPill.value) {
              selectedValues.push(checkedPill.value.trim());
            }
            continue;
          }

          const checkedSwatch = containerEl.querySelector(".option-item.color-palette .swatch-input:checked");
          if (checkedSwatch) {
            const shopifyValue = checkedSwatch.getAttribute("data-option-shopify-value");

            if (shopifyValue) {
              selectedValues.push(shopifyValue.trim());
            }

            continue;
          }

          const optionSelectors = containerEl.querySelectorAll(".option-selector");
          if (optionSelectors.length > 0) {
            for (let k = 0; k < optionSelectors.length; k++) {
              const selectEl = optionSelectors[k];
              const selectedOption = selectEl.options[selectEl.selectedIndex];

              if (selectedOption && selectedOption.text) {
                selectedValues.push(selectedOption.text.trim());
              }
            }

            continue;
          }

          const checkboxInput = containerEl.querySelector(".checkbox-input");
          if (checkboxInput) {
            if (checkboxInput.checked) {
              selectedValues.push("Yes");
            } else {
              selectedValues.push("No");
            }

            continue;
          }
        }
      }

      // console.log( selectedValues );
      return selectedValues.join(" / ");
    }

    getAdvancedRawSelect(productId, changedWrapperEl) {
      const optionRenderIndex = changedWrapperEl.getAttribute("data-option-render-index");

      if (optionRenderIndex) {
        const selectByRenderIndex = this.querySelector(
          `select.raw-data#options-for-${productId}-${optionRenderIndex}`
        );

        if (selectByRenderIndex) {
          return selectByRenderIndex;
        }
      }

      const optionContainer = changedWrapperEl.closest(".option-container");
      if (optionContainer) {
        let sibling = optionContainer.nextElementSibling;

        while (sibling) {
          if (sibling.matches && sibling.matches(`select.raw-data[id^="options-for-${productId}"]`)) {
            return sibling;
          }

          sibling = sibling.nextElementSibling;
        }
      }

      const fallbackSelect = this.querySelector(`select.raw-data[id^="options-for-${productId}"]`);
      if (fallbackSelect) {
        return fallbackSelect;
      }

      return null;
    }

    handleSwatchPreview(swatchChanged) {
      // Update the preview shown in the option title for the selected option
      const label = swatchChanged.closest('.swatch-label');
      if (label) {
        const optionContainer = swatchChanged.closest('.option-container');
        const titleWrapper = optionContainer.querySelector('.option-title-wrapper');

        if (titleWrapper) {
          const previewImage = titleWrapper.querySelector('.option-selected-swatch-image');
          const previewColor = titleWrapper.querySelector('.option-selected-swatch-color');
          const previewText = titleWrapper.querySelector('.option-selected-swatch-text');

          // check for swatch image or color
          const imgEl = label.querySelector('img.swatch-image');
          const swatchValueEl = label.querySelector('.swatch-value');
          const displayName = label.getAttribute('data-swatch-name');

          if (previewText) {
            previewText.textContent = displayName;
          }

          if (imgEl && imgEl.src) {
            if (previewImage) {
              previewImage.src = imgEl.src;
              previewImage.alt = displayName || '';
              previewImage.style.display = '';
            }
            if (previewColor) {
              previewColor.style.display = 'none';
            }
          } else if (swatchValueEl) {
            const bgColor = window.getComputedStyle(swatchValueEl).backgroundColor;
            if (previewColor) {
              previewColor.style.background = bgColor;
              previewColor.style.display = '';
            }
            if (previewImage) {
              previewImage.style.display = 'none';
            }
          }
        }
      }
    }

    handleSwatchClick(swatchChanged, wrapperEl) {
      // close menu of swatch options after selection — hide labels and toggle view more button
      const label = swatchChanged.closest('.swatch-label');
      const swatchOptions = label.closest('.option-item.color-palette');
      if (swatchOptions) {
        // hide all swatch options
        const allSwatchOptions = swatchOptions.querySelectorAll('.swatch-label');
        if (allSwatchOptions.length) {
          allSwatchOptions.forEach((option) => {
            option.classList.add('hide');
          });
        }

        // hide view more button after clicking swatch
        const viewMore = swatchOptions.querySelector('.option-wrapper__view-more-wrapper');
        if (viewMore) {
          viewMore.classList.remove('hide');
        }

        // collapse the option item container
        swatchOptions.classList.add('hide');
      }

      // Recalculate collapsible height
      this.recalculateCollapsibleHeight(wrapperEl);

      // remove is-open class from all option containers
      const allOptionContainers = this.querySelectorAll('.option-container');
      if (allOptionContainers.length) {
        allOptionContainers.forEach((option) => {
          if (option.classList.contains('is-open')) {
            option.classList.remove('is-open');
          }
        });
      }

      // scroll the page back to the page wrapper for products
      if ( wrapperEl != null ) {
        // when on collection page...
        const productRow = wrapperEl.closest('.product-row');
        if ( productRow ) {
          productRow.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          return;
        }

        const target = window.customProductOptionSettings?.back_to_top_target || "";
        if ( target != null && target != "" ) {
          const pageContent = wrapperEl.closest( target );
          if ( pageContent != null ) {
            pageContent.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }
    }

    getBaseSelectedVariant() {
      if (!this.products.length) {
        return null;
      }

      const baseProduct = this.products[0];
      let selectedOptionEl = this.querySelector(`[data-product-option-for="${baseProduct.id}"]:not(.hidden) select.raw-data`);
      if (!selectedOptionEl) {
        // BASIC-product layout (core-product-options.liquid) renders the
        // raw-data <select> as a sibling of the option wrappers, so the
        // scoped selector above misses it. Fall back to locating it by id
        // at the picker root.
        selectedOptionEl = this.querySelector(`select.raw-data#options-for-${baseProduct.id}`);
      }
      if (!selectedOptionEl) {
        return null;
      }

      const selectedVariantId = selectedOptionEl.value;
      if (!selectedVariantId) {
        return null;
      }

      return this.findVariantById(selectedVariantId, baseProduct.variants);
    }

    getSelectedImageOptions() {
      const selectedOptions = {};
      const visibleWrappers = this.querySelectorAll('.option-wrapper:not(.hidden)');

      visibleWrappers.forEach((wrapper) => {
        const rawRepeatIndex = wrapper.getAttribute('data-option-render-index') || '';
        const normalizedRepeatIndex = this.normalizeOptionToken(rawRepeatIndex);
        const repeatIndex = normalizedRepeatIndex && normalizedRepeatIndex !== '1'
          ? normalizedRepeatIndex
          : '';

        const dropdowns = wrapper.querySelectorAll('.option-selector');
        dropdowns.forEach((selectEl) => {
          const key = selectEl.getAttribute('data-option-metafield-name');
          const value = selectEl.value;

          if (!key || !value) {
            return;
          }

          if (!selectedOptions[key]) {
            selectedOptions[key] = [];
          }

          selectedOptions[key].push({
            value: this.normalizeOptionToken(value),
            repeat: this.normalizeOptionToken(repeatIndex)
          });
        });

        const swatches = wrapper.querySelectorAll('.swatch-input:checked');
        swatches.forEach((swatch) => {
          const key = swatch.getAttribute('data-option-metafield-name');
          const value = swatch.getAttribute('data-option-value');

          if (!key || !value) {
            return;
          }

          if (!selectedOptions[key]) {
            selectedOptions[key] = [];
          }

          selectedOptions[key].push({
            value: this.normalizeOptionToken(value),
            repeat: this.normalizeOptionToken(repeatIndex)
          });
        });
      });

      return selectedOptions;
    }

    doesImageConditionMatchSelection(condition, selections = []) {
      if (!condition || !selections.length) {
        return false;
      }

      return selections.some((selection) => {
        if (!selection || selection.value !== condition.value) {
          return false;
        }

        if (!condition.repeat) {
          return true;
        }

        const selectionRepeat = this.normalizeOptionToken(selection.repeat);
        return condition.repeat === selectionRepeat || condition.repeat === `repeat-${selectionRepeat}`;
      });
    }

    doesGalleryImageMatchSelection(galleryImage, baseSku, selectedOptions = {}) {
      if (!galleryImage) {
        return false;
      }

      if (!galleryImage.isOptionLinked) {
        return true;
      }

      const parsed = galleryImage.parsed;
      if (!parsed || parsed.sku !== baseSku || !parsed.conditions.length) {
        return false;
      }

      for (let i = 0; i < parsed.conditions.length; i++) {
        const condition = parsed.conditions[i];
        const selections = selectedOptions[condition.key] || [];

        if (!this.doesImageConditionMatchSelection(condition, selections)) {
          return false;
        }
      }

      return true;
    }

    findBestMatchingGalleryImage() {
      const baseVariant = this.getBaseSelectedVariant();
      if (!baseVariant || !baseVariant.sku || !this.galleryImages.length) {
        this.lastCandidateMatches = [];
        return null;
      }

      const sku = this.normalizeOptionToken(baseVariant.sku);
      const selectedOptions = this.getSelectedImageOptions();
      const candidateMatches = [];

      let bestMatch = null;

      for (let i = 0; i < this.galleryImages.length; i++) {
        const galleryImage = this.galleryImages[i];
        const parsed = galleryImage.parsed;

        if (!parsed || parsed.sku !== sku || !parsed.conditions.length) {
          continue;
        }

        let matchedConditions = 0;
        let unmatchedConditions = 0;

        for (let j = 0; j < parsed.conditions.length; j++) {
          const condition = parsed.conditions[j];
          const selections = selectedOptions[condition.key] || [];

          if (this.doesImageConditionMatchSelection(condition, selections)) {
            matchedConditions += 1;
          } else {
            unmatchedConditions += 1;
          }
        }

        const score = matchedConditions;
        const isFullMatch = unmatchedConditions === 0;
        const matchType = matchedConditions <= 0
          ? 'none'
          : isFullMatch
            ? 'full'
            : 'partial';
        const candidateMatch = {
          ...galleryImage,
          score,
          isFullMatch,
          matchType,
          matchedConditions,
          unmatchedConditions,
          conditionCount: parsed.conditions.length,
          angle: parsed.angle || ''
        };

        candidateMatches.push(candidateMatch);

        if (matchedConditions <= 0) {
          continue;
        }

        if (
          !bestMatch ||
          score > bestMatch.score ||
          (score === bestMatch.score && isFullMatch && !bestMatch.isFullMatch) ||
          (score === bestMatch.score && isFullMatch === bestMatch.isFullMatch && parsed.conditions.length > bestMatch.conditionCount) ||
          (
            score === bestMatch.score &&
            isFullMatch === bestMatch.isFullMatch &&
            parsed.conditions.length === bestMatch.conditionCount &&
            this.getImageAnglePriority(parsed.angle) < this.getImageAnglePriority(bestMatch.angle)
          )
        ) {
          bestMatch = candidateMatch;
        }
      }

      this.lastCandidateMatches = candidateMatches.sort((a, b) => {
        if (b.score !== a.score) {
          return b.score - a.score;
        }

        if (a.isFullMatch !== b.isFullMatch) {
          return a.isFullMatch ? -1 : 1;
        }

        if (b.conditionCount !== a.conditionCount) {
          return b.conditionCount - a.conditionCount;
        }

        return this.getImageAnglePriority(a.angle) - this.getImageAnglePriority(b.angle);
      });

      return bestMatch;
    }

    updateGalleryVisibility(matchedImage = null) {
      if (!this.galleryImages.length) {
        this.loadGalleryImages();
      }

      const baseVariant = this.getBaseSelectedVariant();
      const baseSku = baseVariant?.sku ? this.normalizeOptionToken(baseVariant.sku) : '';
      const selectedOptions = this.getSelectedImageOptions();
      const { mainSlider, thumbSlider } = this.getScopedGalleryEls();
      const shouldKeepMediaGalleryVisible = matchedImage?.galleryType === 'media-gallery' && matchedImage?.matchType === 'partial';

      this.galleryImages.forEach((galleryImage) => {
        const shouldShow = (shouldKeepMediaGalleryVisible && galleryImage.galleryType === 'media-gallery')
          || this.doesGalleryImageMatchSelection(galleryImage, baseSku, selectedOptions)
          || (matchedImage && galleryImage.mediaId === matchedImage.mediaId);

          console.log('Gallery image:', galleryImage.fileName, 'Should show:', shouldShow);

        if (galleryImage.galleryType === 'product-images') {
          const thumb = thumbSlider?.querySelector(`.product__thumb[data-id="${galleryImage.mediaId}"]`);
          const thumbItem = thumb?.closest('.product__thumb-item');
          const slide = mainSlider?.querySelector(`.product-main-slide[data-index="${galleryImage.index}"]`);

          if (thumbItem) {
            thumbItem.classList.toggle('hide', !shouldShow);
          }

          if (slide) {
            slide.classList.toggle('hide', !shouldShow);
          }
        } else if (galleryImage.galleryType === 'media-gallery') {
          const { mediaGalleryEl } = this.getScopedGalleryEls();
          const slide = mediaGalleryEl?.querySelector(`.media-gallery__slide[data-media-id="${galleryImage.mediaId}"]`);
          const thumb = mediaGalleryEl?.querySelector(`.media-gallery__thumb[data-media-id="${galleryImage.mediaId}"]`);

          if (slide) {
            slide.classList.toggle('hide', !shouldShow);
          }

          if (thumb) {
            thumb.classList.toggle('hide', !shouldShow);
          }
        }
      });

      if (mainSlider && window.Flickity && typeof window.Flickity.data === 'function') {
        const flickity = window.Flickity.data(mainSlider);
        if (flickity) {
          flickity.reloadCells();
          const selectedElement = flickity.selectedElement;
          const currentSlideIsHidden = selectedElement?.classList.contains('hide');
          const firstVisibleSlide = mainSlider.querySelector('.product-main-slide:not(.hide)');
          const firstVisibleIndex = firstVisibleSlide
            ? parseInt(firstVisibleSlide.getAttribute('data-index'), 10)
            : -1;

          if (
            Number.isInteger(firstVisibleIndex) &&
            firstVisibleIndex >= 0 &&
            (!matchedImage || currentSlideIsHidden)
          ) {
            const targetIndex = mainSlider.dataset.sectionalProductRowSlider === 'true'
              ? this.getProductImagesFlickityCellIndex(mainSlider, firstVisibleSlide)
              : firstVisibleIndex;

            if (targetIndex >= 0) {
              flickity.select(targetIndex, false, true);
            }
          }

          flickity.resize();
        }
      }
    }

    buildExpectedImageFileName(baseVariant = null, selectedOptions = {}) {
      if (!baseVariant || !baseVariant.sku) {
        return 'none';
      }

      const segments = [String(baseVariant.sku).trim()];
      const optionKeys = Object.keys(selectedOptions).sort();

      optionKeys.forEach((key) => {
        const selections = selectedOptions[key] || [];
        selections.forEach((selection) => {
          if (!selection?.value) {
            return;
          }

          let segment = `${key}--${selection.value}`;
          if (selection.repeat) {
            segment += `__${selection.repeat}`;
          }

          segments.push(segment);
        });
      });

      return `${segments.join('__')}.jpg`;
    }

    updateImageMatchDebug(matchedImage = null) {
      const debugEl = this.querySelector('[data-image-match-debug]');
      if (!debugEl) {
        return;
      }

      const baseVariant = this.getBaseSelectedVariant();
      const selectedOptions = this.getSelectedImageOptions();
      const expectedFileName = this.buildExpectedImageFileName(baseVariant, selectedOptions);
      const selectedOptionsSummary = Object.keys(selectedOptions).map((key) => {
        const values = selectedOptions[key].map((selection) => {
          if (selection.repeat) {
            return `${selection.value} (${selection.repeat})`;
          }

          return selection.value;
        });

        return `${key}: ${values.join(', ')}`;
      });

      const matchedConditions = matchedImage?.parsed?.conditions?.map((condition) => {
        return `${condition.key}=${condition.value}${condition.repeat ? ` (${condition.repeat})` : ''}`;
      }) || [];
      const candidateFileNames = (this.lastCandidateMatches || []).map((candidate) => {
        const angleText = candidate.angle ? ` ${candidate.angle}` : '';
        return `${candidate.fileName} [${candidate.matchType} ${candidate.matchedConditions}/${candidate.conditionCount}${angleText}]`;
      });
      const allGalleryFileNames = this.galleryImages.map((image) => {
        return image.fileName || image.src || 'unknown';
      });

      const matchedSource = matchedImage?.isCdn ? 'cdn' : (matchedImage ? 'shopify' : 'none');
      const cdnImageCount = (this.cdnImages || []).length;

      const lines = [
        `Base SKU: ${baseVariant?.sku || 'none'}`,
        `Selected options: ${selectedOptionsSummary.length ? selectedOptionsSummary.join(' | ') : 'none'}`,
        `Expected file name: ${expectedFileName}`,
        `Matched image: ${matchedImage?.fileName || 'none'}`,
        `Match source: ${matchedSource}`,
        `Match type: ${matchedImage?.matchType || 'none'}`,
        `Match score: ${matchedImage ? `${matchedImage.matchedConditions}/${matchedImage.conditionCount}` : 'none'}`,
        `Matched conditions: ${matchedConditions.length ? matchedConditions.join(' | ') : 'none'}`,
        `CDN manifest images: ${cdnImageCount}`,
        `Candidate images: ${candidateFileNames.length ? candidateFileNames.join(' | ') : 'none'}`,
        `All gallery images: ${allGalleryFileNames.length ? allGalleryFileNames.join(' | ') : 'none'}`
      ];

      debugEl.innerHTML = `${lines.join('<br>')}`;
    }

    updateMetafieldRenderDebug() {
      const debugEl = this.querySelector('[data-metafield-render-debug]');
      if (!debugEl) {
        return;
      }

      const debugBlocks = this.querySelectorAll('[data-metafield-debug-block]');
      const lines = [];

      debugBlocks.forEach((block) => {
        const content = block.innerHTML.trim();
        if (content) {
          lines.push(content);
        }
      });

      if (!lines.length) {
        debugEl.innerHTML = 'Metafield render debug: no debug blocks found';
        return;
      }

      debugEl.innerHTML = lines.join('<br>');
    }

    syncGalleryImage() {
      this.cdnLog('syncGalleryImage() fired. cdnImages.length =', (this.cdnImages || []).length);
      if (!this.galleryImages.length) {
        this.loadGalleryImages();
      }

      // CDN match takes precedence whenever a per-variant image is available
      // in the CDN manifest — it is by definition more specific than a generic
      // Shopify-hosted fallback. Smaller products with no manifest fall through
      // untouched because cdnImages is empty.
      const cdnMatch = this.findBestCdnMatch();
      const cdnSlotMatch = this.updateCdnGallerySlot(cdnMatch);

      const shopifyMatch = this.findBestMatchingGalleryImage();
      const matchedImage = cdnSlotMatch || shopifyMatch;
      this.cdnLog('syncGalleryImage() winning match source:',
        cdnSlotMatch ? 'cdn' : (shopifyMatch ? 'shopify' : 'none'),
        '- file:', matchedImage?.fileName || 'n/a'
      );
      this.updateGalleryVisibility(matchedImage);
      const { mediaGalleryEl, mainSlider, thumbSlider } = this.getScopedGalleryEls();
      const productGalleryThumb = matchedImage
        ? thumbSlider?.querySelector(`.product__thumb[data-id="${matchedImage.mediaId}"]`)
        : null;

      if (!mediaGalleryEl && !thumbSlider) {
        this.updateImageMatchDebug(null);
        return;
      }

      if (!matchedImage) {
        this.updateImageMatchDebug(null);
        const firstVisibleThumb = thumbSlider?.querySelector( '.product__thumb-item:not(.hide) .product__thumb' );
        if (firstVisibleThumb) {
          const firstVisibleIndex = parseInt(firstVisibleThumb.getAttribute('data-index'), 10);

          if (
            mainSlider &&
            Number.isInteger(firstVisibleIndex) &&
            window.Flickity &&
            typeof window.Flickity.data === 'function'
          ) {
            const flickity = window.Flickity.data(mainSlider);
            if (flickity && typeof flickity.select === 'function') {
              const targetIndex = mainSlider.dataset.sectionalProductRowSlider === 'true'
                ? this.getProductImagesFlickityCellIndex(mainSlider, firstVisibleThumb.closest('.product__thumb-item'))
                : firstVisibleIndex;

              if (targetIndex >= 0) {
                flickity.select(targetIndex, false, true);
              }

              flickity.resize();
            }
          }

          if (mainSlider?.dataset.sectionalProductRowSlider !== 'true') {
            firstVisibleThumb.click();
          }
        }

        return;
      }

      this.updateImageMatchDebug(matchedImage);

      if (matchedImage.galleryType === 'media-gallery' && mediaGalleryEl && typeof mediaGalleryEl.goToSlide === 'function') {
        const matchedSlide = mediaGalleryEl.querySelector(`.media-gallery__slide[data-media-id="${matchedImage.mediaId}"]`);
        const matchedThumb = mediaGalleryEl.querySelector(`.media-gallery__thumb[data-media-id="${matchedImage.mediaId}"]`);
        const activeSlide = mediaGalleryEl.querySelector('.media-gallery__slide--active');
        const activeSlideIsHidden = activeSlide?.classList.contains('hide');
        const shouldDirectlyActivate = matchedImage.matchType === 'partial' || activeSlideIsHidden || !activeSlide;

        if (matchedSlide) {
          matchedSlide.classList.remove('hide');
        }

        if (matchedThumb) {
          matchedThumb.classList.remove('hide');
        }

        if (matchedImage.index === mediaGalleryEl.currentIndex || shouldDirectlyActivate) {
          mediaGalleryEl.querySelectorAll('.media-gallery__slide').forEach((slide) => {
            slide.classList.toggle('media-gallery__slide--active', slide.getAttribute('data-media-id') === matchedImage.mediaId);
          });

          mediaGalleryEl.querySelectorAll('.media-gallery__thumb').forEach((thumb) => {
            thumb.classList.toggle('media-gallery__thumb--active', thumb.getAttribute('data-media-id') === matchedImage.mediaId);
          });

          mediaGalleryEl.currentIndex = matchedImage.index;

          if (matchedSlide) {
            matchedSlide.style.transform = 'translateX(0)';
          }

          if (matchedThumb) {
            matchedThumb.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }

          return;
        }

        const direction = matchedImage.index > mediaGalleryEl.currentIndex ? 'next' : 'prev';
        mediaGalleryEl.goToSlide(matchedImage.index, direction);
        return;
      }

      if (matchedImage.galleryType === 'product-images') {
        if (matchedImage.isCdn) {
          // Flickity's childNav was initialized before the CDN thumb existed,
          // so its internal thumb→slide wiring skips the injected element. We
          // also can't trust updateGalleryVisibility's fallback flickity.select
          // because it only fires when the currently selected slide is hidden,
          // and a generic Shopify hero shot stays visible by default. Drive
          // Flickity directly to the CDN cell by its real cell index.
          this.activateCdnSlideInProductImages();
          return;
        }
        if (productGalleryThumb) {
          const thumbIndex = parseInt(productGalleryThumb.getAttribute('data-index'), 10);
          const { mainSlider } = this.getScopedGalleryEls();

          if (
            mainSlider &&
            Number.isInteger(thumbIndex) &&
            window.Flickity &&
            typeof window.Flickity.data === 'function'
          ) {
            const flickity = window.Flickity.data(mainSlider);

            if (flickity && typeof flickity.select === 'function') {
              const targetIndex = mainSlider.dataset.sectionalProductRowSlider === 'true'
                ? this.getProductImagesFlickityCellIndex(mainSlider, productGalleryThumb.closest('.product__thumb-item'))
                : thumbIndex;

              if (targetIndex >= 0) {
                flickity.select(targetIndex, false, true);
              }

              flickity.resize();
            }
          }

          return;
        }
      }

      if (mediaGalleryEl) {
        const thumb = mediaGalleryEl.querySelector(`.media-gallery__thumb[data-media-id="${matchedImage.mediaId}"]`);
        if (thumb) {
          thumb.click();
        }
      }
    }

    getSelectedVariants() {
      const selectedVariants = [];
      const productType = (this.getAttribute("data-product-type") || "").trim().toUpperCase();

      let qty = 1;
      const scope = this.closest('.product-row') || this.closest('.product-section') || document;
      const qtyInput = scope.querySelector( ".js-qty__wrapper .js-qty__num" );
      if ( qtyInput != null ) {
        const qtyVal = qtyInput.value;
        if ( qtyVal != null ) {
          const qtyInt = parseInt( qtyVal );
          if ( !isNaN( qtyInt ) ) {
            qty = qtyInt;
          }
        }
      }

      for (let i = 0; i < this.products.length; i++) {
        const product = this.products[i];
        const productId = product.id;
        const variants = product.variants;

        // when ADD_ONS type, first index = base product | any index after is add on
        const productQty = ( productType === "ADD_ONS" && i > 0 ) ? 1 : qty;

        if (productType === "BASIC") {
          const selectableOptionEl = this.querySelector(`select.raw-data#options-for-${productId}`);

          if (!selectableOptionEl) {
            continue;
          }

          const selectedOptionEl = selectableOptionEl.options[selectableOptionEl.selectedIndex];
          if (!selectedOptionEl || !selectedOptionEl.value) {
            continue;
          }

          const selectedVariantId = selectedOptionEl.value;
          const variant = this.findVariantById(selectedVariantId, variants);

          if (!variant) {
            continue;
          }

          const props = {};
          // no need to count props on this version
          // const visibleWrappers = this.querySelectorAll(
          //   `[data-product-option-for="${productId}"]:not(.hidden)`
          // );

          // for (let j = 0; j < visibleWrappers.length; j++) {
          //   const wrapperEl = visibleWrappers[j];

          //   const checkedSwatch = wrapperEl.querySelector(".swatch-input:checked");
          //   if (checkedSwatch) {
          //     const key =
          //       checkedSwatch.getAttribute("data-related-to-option") ||
          //       checkedSwatch.getAttribute("data-option-metafield-name");

          //     const value =
          //       checkedSwatch.getAttribute("data-option-value") ||
          //       checkedSwatch.getAttribute("data-option-shopify-value");

          //     if (key && value) {
          //       props[key] = value;
          //     }
          //   }

          //   const checkedPill = wrapperEl.querySelector(".pill-input:checked");
          //   if (checkedPill) {
          //     const key =
          //       checkedPill.getAttribute("data-related-to-option") ||
          //       checkedPill.getAttribute("name");

          //     const value = checkedPill.value;

          //     if (key && value) {
          //       props[key] = value;
          //     }
          //   }

          //   const dropdowns = wrapperEl.querySelectorAll(".option-selector");
          //   for (let p = 0; p < dropdowns.length; p++) {
          //     const dropdown = dropdowns[p];
          //     const selectedDropdownOption = dropdown.options[dropdown.selectedIndex];

          //     if (!selectedDropdownOption) {
          //       continue;
          //     }

          //     const key =
          //       dropdown.getAttribute("data-related-to-option") ||
          //       dropdown.getAttribute("data-option-metafield-name") ||
          //       dropdown.getAttribute("name");

          //     const value = selectedDropdownOption.value
          //       ? selectedDropdownOption.text.trim()
          //       : "";

          //     if (key && value) {
          //       props[key] = value;
          //     }
          //   }

          //   const checkboxInput = wrapperEl.querySelector(".checkbox-input");
          //   if (checkboxInput) {
          //     const key =
          //       checkboxInput.getAttribute("data-related-to-option") ||
          //       checkboxInput.getAttribute("name");

          //     if (key) {
          //       if (checkboxInput.checked) {
          //         props[key] = "Yes";
          //       } else {
          //         props[key] = "No";
          //       }
          //     }
          //   }
          // }
          
          selectedVariants.push({
            id: parseInt(selectedVariantId),
            quantity: productQty,
            properties: props,
            product_id: productId,
            related_variant: variant
          });
        } else {
          const selectableOptionEls = this.querySelectorAll(
            `[data-product-option-for="${productId}"]:not(.hidden) .raw-data`
          );

          for (let j = 0; j < selectableOptionEls.length; j++) {
            const selectableOptionEl = selectableOptionEls[j];
            const selectedOptionEl = selectableOptionEl.options[selectableOptionEl.selectedIndex];
            const selectedVariantId = selectedOptionEl.value;

            const variant = this.findVariantById(selectedVariantId, variants);
            if (!variant) {
              continue;
            }

            const props = {};
            const relatedWrappers = this.querySelectorAll(
              `[data-product-option-for="${productId}"]:not(.hidden)`
            );

            for (let p = 0; p < relatedWrappers.length; p++) {
              const wrapperEl = relatedWrappers[p];

              const checkedSwatches = wrapperEl.querySelectorAll(".swatch-input:checked");
              for (let s = 0; s < checkedSwatches.length; s++) {
                const radio = checkedSwatches[s];

                if (radio.classList.contains("pill-input")) {
                  continue;
                }

                const key =
                  radio.getAttribute("data-related-to-option") ||
                  radio.getAttribute("data-option-metafield-name");

                const value =
                  radio.getAttribute("data-option-value") ||
                  radio.getAttribute("data-option-shopify-value");

                if (key && value) {
                  props[key] = value;
                }
              }

              const checkedPills = wrapperEl.querySelectorAll(".pill-input:checked");
              for (let s = 0; s < checkedPills.length; s++) {
                const radio = checkedPills[s];

                const key =
                  radio.getAttribute("data-related-to-option") ||
                  radio.getAttribute("data-option-metafield-name") ||
                  radio.getAttribute("name");

                const value = radio.value;

                if (key && value) {
                  props[key] = value;
                }
              }
            }

            selectedVariants.push({
              id: parseInt(selectedVariantId),
              quantity: productQty,
              properties: props,
              product_id: productId,
              related_variant: variant
            });
          }
        }
      }

      console.log( selectedVariants );

      return selectedVariants;
    }

    findVariantById(variantId, variants) {
      let variant = null;
      for (let i = 0; i < variants.length; i++) {
        const currentVariant = variants[i];
        const currentVariantId = currentVariant.id;

        if (variantId == currentVariantId) {
          variant = currentVariant;
          break;
        }
      }

      return variant;
    }

    showHiddenSwatches() {
      this.optionTitleWrappers = this.querySelectorAll('.option-title-wrapper');
      if (this.optionTitleWrappers.length <= 0) return;

      this.optionTitleWrappers.forEach((titleWrapper) => {
        if (this.boundOptionTitles.has(titleWrapper)) return;
        this.boundOptionTitles.add(titleWrapper);
        titleWrapper.addEventListener('click', () => {
          const optionContainer = titleWrapper.closest('.option-container');
          if (!optionContainer) return;

          // close option containers
          this.closeOptionContainers(optionContainer);

          const wrapper = optionContainer.closest('.option-wrapper');
          if (!wrapper) return;
          const optionItem = optionContainer.querySelector('.option-item.color-palette');
          if (!optionItem) return;

          // toggle hidden swatches
          this.toggleHiddenSwatches(optionItem);

          // Recalculate collapsible height for this option
          this.recalculateCollapsibleHeight(optionItem);
        });
      });
    }

    toggleHiddenSwatches(optionItem) {
      const isHidden = optionItem.classList.contains('hide');

      if (!isHidden) {
        optionItem.classList.add('hide');
        return;
      }

      optionItem.classList.remove('hide');

      const tierWrappers = optionItem.querySelectorAll('.options-tier-wrapper');
      const viewMore = optionItem.querySelector('.option-wrapper__view-more-wrapper');

      if (!tierWrappers.length) {
        const optionItems = optionItem.querySelectorAll('.swatch-label');
        optionItems.forEach((label, index) => {
          label.classList.toggle('hide', index >= 10);
        });

        if (viewMore) {
          viewMore.classList.toggle('hide', optionItems.length <= 10);
        }

        return;
      }

      let visibleCount = 0;
      let totalSwatches = 0;

      tierWrappers.forEach((tierWrapper) => {
        const heading = tierWrapper.querySelector('.tier-heading');
        const labels = tierWrapper.querySelectorAll('.swatch-label');
        let tierVisibleCount = 0;

        totalSwatches += labels.length;

        labels.forEach((label) => {
          const shouldShow = visibleCount < 10;
          label.classList.toggle('hide', !shouldShow);

          if (shouldShow) {
            visibleCount += 1;
            tierVisibleCount += 1;
          }
        });

        if (heading) {
          heading.classList.toggle('hidden', tierVisibleCount === 0);
        }

        tierWrapper.classList.toggle('hide', tierVisibleCount === 0);
      });

      if (viewMore) {
        viewMore.classList.toggle('hide', totalSwatches <= 10);

        if (!viewMore.classList.contains('is-active')) {
          viewMore.addEventListener('click', async (event) => {
            event.stopPropagation();

            const totalMetaobjectsRaw = viewMore.getAttribute('data-total-metaobjects') || '0';
            const totalMetaobjects = parseInt(totalMetaobjectsRaw, 10);

            if (totalMetaobjects > 50) {
              await loadMoreSwatchesFromViewAll(viewMore);
            }

            const refreshedTierWrappers = optionItem.querySelectorAll('.options-tier-wrapper');

            if (refreshedTierWrappers.length) {
              refreshedTierWrappers.forEach((tierWrapper) => {
                const heading = tierWrapper.querySelector('.tier-heading');
                const labels = tierWrapper.querySelectorAll('.swatch-label');
                const hasVisibleLabels = labels.length > 0;

                labels.forEach((label) => label.classList.remove('hide'));

                if (heading) {
                  heading.classList.toggle('hidden', !hasVisibleLabels);
                }

                tierWrapper.classList.toggle('hide', !hasVisibleLabels);
              });
            } else {
              optionItem.querySelectorAll('.swatch-label').forEach((label) => {
                label.classList.remove('hide');
              });
            }

            viewMore.classList.add('hide');
            this.recalculateCollapsibleHeight(optionItem);
          });

          viewMore.classList.add('is-active');
        }
      }
    }

    closeOptionContainers(optionContainer) {
      const allOptionContainers = this.querySelectorAll('.option-container');
      allOptionContainers.forEach((option) => {
        // only close if its not the current one being clicked
        if (option === optionContainer) return;
        option.classList.remove('is-open');
        const colorPalette = option.querySelector('.option-item.color-palette');
        if (colorPalette) {
          // hides other open color-palette classes when opening a new one
          colorPalette.classList.add('hide');
          const swatchLabels = colorPalette.querySelectorAll('.swatch-label');
          swatchLabels.forEach((label) => {
            label.classList.add('hide');
          });

          // if there is a view more button, hide it on other options when opening a new one
          const viewMoreOption = colorPalette.querySelector('.option-wrapper__view-more-wrapper');
          if (viewMoreOption) {
            viewMoreOption.classList.add('hide');
          }
        }
      });

      // toggle is-open class on the clicked container
      if (!optionContainer.classList.contains('is-open')) {
        optionContainer.classList.add('is-open');
      } else {
        optionContainer.classList.remove('is-open');
      }
    }

    recalculateCollapsibleHeight(wrapper) {
      setTimeout(() => {
        const container = wrapper.closest('.collapsible-content');
        if (container) {
          const inner = container.querySelector('.collapsible-content__inner');
          if (inner) {
            if (!container.style.height) {
              container.style.height = `${inner.offsetHeight}px`;
              container.offsetHeight;
            }

            if (window.theme?.utils?.prepareTransition) {
              theme.utils.prepareTransition(container, () => {
                if ( container.style.height != "auto") {
                  container.style.height = `${inner.offsetHeight}px`;
                }
              });
            } else {
              if ( container.style.height != "auto") {
                  container.style.height = `${inner.offsetHeight}px`;
                }
            }
          }
        }
      }, 50);
    }

    updateSpecificationTable( productId, variantId ) {
      const scope = this.closest('.product-row') || this.closest('.product-section') || this;
      const specificationTables = scope.querySelectorAll('specification-table');

      for (let i = 0; i < specificationTables.length; i++) {
          const specificationTable = specificationTables[i];

          if ( String(specificationTable.dataset.productId) === String(productId) ) {
            const rebuildTable = () => {
              specificationTable.dataset.selectedVariantId = variantId;

              if ( typeof specificationTable.rebuild === 'function' ) {
                specificationTable.rebuild();
              }
            };

            if ( typeof specificationTable.rebuild === 'function' ) {
              rebuildTable();
            } else if ( window.customElements?.whenDefined ) {
              customElements.whenDefined('specification-table').then(rebuildTable);
            }

            break;
          }
      }
    }

    updatePrice( productId, variantId ) {
      // console.log( "RUN UPDATE PRICE" , productId , variantId );
      let productSection = null;
      let finalPrice = null;
      let basePrice = null;
      let formattedFinalPrice = "";
      let formattedBasePrice = "";

      const scope = this.closest('.product-row') || this.closest('.product-section') || document;

      productSection = scope.querySelector(
        `.product-section[data-product-id="${productId}"]`
      );

      if (!productSection) {
          return;
      }

      const finalPriceElement = productSection.querySelector("[data-final-price]");
      const basePriceElement = productSection.querySelector("[data-base-price]");

      finalPrice = pricingManager.getPriceForProductVariant(variantId) ?? "0.0";
      basePrice = pricingManager.getBasePriceForProductVariant(variantId) ?? "0.0";

      if (finalPrice !== null) {
        formattedFinalPrice = pricingManager.formatMoney(finalPrice);
      }

      if (basePrice !== null) {
        formattedBasePrice = pricingManager.formatMoney(basePrice);
      }

      if (finalPriceElement) {
        finalPriceElement.innerHTML = formattedFinalPrice;
      }

      if (basePriceElement) {
        basePriceElement.innerHTML = formattedBasePrice;
      }

      if (finalPrice !== null) {
        if (basePrice !== null) {
          const basePriceFloat = parseFloat(basePrice);
          const finalPriceFloat = parseFloat(finalPrice);

          if (finalPriceFloat === basePriceFloat) {
            if (finalPriceElement) {
                finalPriceElement.classList.add("hide");
            }

            if (basePriceElement) {
                basePriceElement.classList.remove("product__price--compare");
            }
          } else {
            if (finalPriceElement) {
                finalPriceElement.classList.remove("hide");
            }

            if (basePriceElement) {
                basePriceElement.classList.add("product__price--compare");
            }
          }
        }
      }
    }

    updatePriceV2() {
      let totalFinalPrice = 0;
      let totalBasePrice = 0;
      let hasQuotedItems = false;
      let isUmbrella = false;

      let quoteText = "Quote";
      let quotedItemsText = " + Quoted Items";
      let tbdText = "TBD";

      if (window.customProductOptionSettings) {
        if (window.customProductOptionSettings.language) {
          if (window.customProductOptionSettings.language.quote) {
            quoteText = window.customProductOptionSettings.language.quote;
          }

          if (window.customProductOptionSettings.language.quoted_items) {
            quotedItemsText = window.customProductOptionSettings.language.quoted_items;
          }

          if (window.customProductOptionSettings.language.tbd) {
            tbdText = window.customProductOptionSettings.language.tbd;
          }
        }
      }

      const selectedVariants = this.getSelectedVariants();

      if (selectedVariants.length > 1) {
        isUmbrella = true;
      }

      for (let i = 0; i < selectedVariants.length; i++) {
        const selectedVariant = selectedVariants[i];
        const variantId = selectedVariant.id;
        const productId = selectedVariant.product_id;
        const relatedVariant = selectedVariant.related_variant;

        if (this.isQuoteVariant(relatedVariant)) {
          hasQuotedItems = true;
        }

        const finalPrice = parseFloat(
          pricingManager.getPriceForProductVariantFromProducts(productId, variantId) ?? "0"
        );

        const basePrice = parseFloat(
          pricingManager.getBasePriceForProductVariantFromProducts(productId, variantId) ?? "0"
        );

        totalFinalPrice += finalPrice;
        totalBasePrice += basePrice;
      }

      const productSection = this.closest('.product-row') || this.closest('.product-section');

      if (!productSection) {
        return;
      }

      const finalPriceElement = productSection.querySelector("[data-final-price]");
      const basePriceElement = productSection.querySelector("[data-base-price]");

      if (!isUmbrella) {
        if (hasQuotedItems) {
          if (totalFinalPrice === 0) {
            if (finalPriceElement) {
              finalPriceElement.innerHTML = quoteText;
              finalPriceElement.classList.remove("hide");
            }

            if (basePriceElement) {
              basePriceElement.innerHTML = "";
              basePriceElement.classList.add("hide");
              basePriceElement.classList.remove("product__price--compare");
            }

            return;
          }
        }
      }

      if (isUmbrella) {
        if (finalPriceElement) {
          if (hasQuotedItems) {
            finalPriceElement.innerHTML =
              pricingManager.formatMoney(totalFinalPrice) + quotedItemsText;
          } else {
            finalPriceElement.innerHTML =
              pricingManager.formatMoney(totalFinalPrice);
          }
        }
      } else {
        if (finalPriceElement) {
          finalPriceElement.innerHTML =
            pricingManager.formatMoney(totalFinalPrice);
        }
      }

      if (basePriceElement) {
        basePriceElement.innerHTML =
          pricingManager.formatMoney(totalBasePrice);
      }

      if (isUmbrella) {
        if (hasQuotedItems) {
          if (finalPriceElement) {
            finalPriceElement.classList.remove("hide");
          }

          if (basePriceElement) {
            basePriceElement.classList.add("hide");
            basePriceElement.classList.remove("product__price--compare");
          }

          return;
        }
      }

      if (totalFinalPrice === totalBasePrice) {
        if (finalPriceElement) {
          finalPriceElement.classList.add("hide");
        }

        if (basePriceElement) {
          basePriceElement.classList.remove("product__price--compare");
          basePriceElement.classList.remove("hide");
        }
      } else {
        if (finalPriceElement) {
          finalPriceElement.classList.remove("hide");
        }

        if (basePriceElement) {
          basePriceElement.classList.add("product__price--compare");
          basePriceElement.classList.remove("hide");
        }
      }
    }

   getCalculatedSku() {
      let calculatedSku = "";

      const baseVariant = this.getBaseSelectedVariant();
      const productType = (this.getAttribute("data-product-type") || "").trim().toUpperCase();

      let baseSku = baseVariant?.sku ?? "";
      if ( typeof( window?.pricingConfig?.getBaseSkuForVariant ) !== "undefined" ) {
        baseSku = pricingConfig.getBaseSkuForVariant(baseVariant?.id)
      }

      const baseProduct = this.products[ 0 ];
      if ( !baseProduct ) {
        calculatedSku = baseSku.trim();
        return calculatedSku;
      }

      const selectedSkuParts = [];
      const selectedInputs = this.querySelectorAll(
        `[data-product-option-for="${baseProduct.id}"]:not(.hidden) input:checked[data-sku], ` +
        `[data-product-option-for="${baseProduct.id}"]:not(.hidden) select option:checked[data-sku]`
      );

      for ( let i = 0; i < selectedInputs.length; i++ ) {
        const input = selectedInputs[ i ];
        let sku = input.getAttribute( 'data-sku' );
        let optionMetafieldName = input.getAttribute( 'data-option-metafield-name' );

        if ( sku == null ) {
          sku = "";
        }

        const skuPart = {
          sku: sku,
          metafieldName: optionMetafieldName
        };

        selectedSkuParts.push( skuPart );
      }

      selectedSkuParts.sort( ( a, b ) => {
        return this.getSkuSortPriority( a.metafieldName ) - this.getSkuSortPriority( b.metafieldName );
      });

      console.log( `SKU BUILDER TYPE: ` , productType );
      console.log( `BASE SKU ["${baseVariant?.id}"]: ` , baseSku );
      console.log( "SKU PARTS: " , selectedSkuParts );

      if ( productType !== "BASIC" ) {
        calculatedSku = [
          baseSku.trim(),
          ...selectedSkuParts.map( ( part ) => part.sku )
        ].join('');
      } else {
        calculatedSku = baseSku.trim();
      }

      return calculatedSku;
    }

    getSkuSortPriority( metafieldName = '' ) {
      let priority = 3;

      if ( metafieldName === 'table_hole' ) {
        priority = 1;
      }

      if ( metafieldName === 'tabletop_pattern' ) {
        priority = 2;
      }

      return priority;
    }

    updateCalculatedSku() {
      const calculatedSku = this.getCalculatedSku();
      const scope = this.closest('.product-row') || this.closest('.product-section') || this;
      const skuEl = scope.querySelector('[data-sku-new]');

      if ( skuEl != null ) {
        skuEl.innerHTML = calculatedSku;
      }

      this.setAttribute( 'data-calculated-sku', calculatedSku );

      return calculatedSku;
    }
    
    isQuoteVariant( variant ) {
      let title = "";

      if ( variant.title ) {
        title = variant.title;
      } else if ( variant.name ) {
        title = variant.name;
      }

      title = title.toLowerCase();

      if (title.indexOf("non-stock") !== -1) {
        return true;
      }

      if (title.indexOf("quote") !== -1) {
        return true;
      }

      if (title.indexOf("custom") !== -1) {
        return true;
      }

      return false;
    }

    formatMoney(cents) {
      const amount = Number(cents) / 100;

      return amount.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD'
      });
    }

    getProductRow() {
      return this.closest('.product-row') || document;
    }

    getScopedGalleryEls() {
      const scope = this.getProductRow();

      return {
        scope,
        mediaGalleryEl: scope.querySelector('media-gallery'),
        mainSlider: scope.querySelector('[data-product-photos]'),
        thumbSlider: scope.querySelector('[data-product-thumbs]')
      };
    }
  }

  customElements.define('custom-variant-picker', CustomVariantPicker);
}
